/**
 * Full-screen arrow playground (Vite lab UX on Shopify).
 * Freeform 3D build → Add to cart (same product mapping as the classic wizard).
 */
(function () {
  const STORAGE_KEY = 'titlex_arrow_playground_v1';
  const STORAGE_VERSION = 1;

  const COLOR_MAP = {
    'bright green': '#2eff3c',
    'od green': '#4e5f4b',
    sand: '#ead8ab',
    gray: '#808080',
    grey: '#808080',
    orange: '#ff8a00',
    red: '#dc2626',
    black: '#000000',
    white: '#ffffff',
    pink: '#ff2d9b',
    magenta: '#ff2d9b',
    hotpink: '#ff2d9b',
    'hot pink': '#ff2d9b',
    purple: '#a54dcf',
    'fire orange': '#f39e05',
    blue: '#005bd3',
    green: '#1f9d3a',
    yellow: '#f4d03f',
    teal: '#008080',
    'neon green': '#2eff3c',
    olive: '#6b7032',
    tan: '#c4a36a',
    navy: '#1b2a6b',
    'dark green': '#1e4632',
    brown: '#6b3f2a',
  };

  const VANE_COLOR_NAMES = [
    'Bright Green',
    'OD Green',
    'Sand',
    'Gray',
    'Orange',
    'Red',
    'Black',
    'White',
    'Pink',
    'Purple',
    'Blue',
  ];

  const NOCK_COLOR_NAMES = [
    'Orange',
    'Bright Green',
    'Black',
    'Green',
    'Red',
    'Blue',
    'Yellow',
    'White',
  ];

  const LIGHTED_NOCK_COLOR_NAMES = ['Bright Green', 'Blue', 'Red'];

  const DEMO_STATE = {
    model: 'machxv',
    spine: '250',
    wrapKey: 'none',
    wrapUrl: null,
    fletchCount: 3,
    vanePrimary: 'Bright Green',
    vaneSecondary: 'OD Green',
    vaneStyle: 'hybrid',
    vanePrimaryUrl: null,
    vaneSecondaryUrl: null,
    nockColor: 'Orange',
    lightedNocks: false,
    lightedNockColor: 'Bright Green',
    insert: true,
    insertFinish: 'brass',
    insertAluminumOd: '6.8mm',
    fieldPoint: true,
    fieldPointFinish: 'stainless',
    fieldPointGrain: 100,
    cutLength: 25,
    helical: 'left',
    /** titlex | wrap_only | diy */
    laborMode: 'titlex',
  };

  const MODEL_LABELS = {
    tx5: 'TX-5',
    tx23: 'TX-23',
    machxv: 'TX-5 Mach XV',
  };

  const MODEL_PICKER_LABELS = { tx5: 'TX-5', tx23: 'TX-23', machxv: 'Mach XV' };
  const MODEL_CLUSTERS = [
    { title: 'Hunting', keys: ['machxv', 'tx5'] },
    { title: 'Target', keys: ['tx23'] },
  ];

  function isTargetModel(model) {
    return model === 'tx23';
  }

  function normalizeGlueInGrain(grain) {
    return parseInt(grain, 10) === 200 ? 200 : 120;
  }

  function targetHardwarePatch(grain) {
    return {
      insert: false,
      fieldPoint: true,
      fieldPointFinish: 'gluein',
      fieldPointGrain: grain,
      nockColor: 'Black',
      lightedNocks: false,
    };
  }

  function huntingUsesGlueIn(hw) {
    const g = parseInt(hw.fieldPointGrain, 10);
    return hw.fieldPointFinish === 'gluein' || g === 120 || g === 200;
  }

  function clearGlueInFromHunting(hw) {
    if (!huntingUsesGlueIn(hw)) return;
    hw.fieldPointFinish = 'stainless';
    hw.fieldPointGrain = 100;
  }

  function normalizeInsertFinish(finish) {
    if (finish === 'stainless' || finish === 'aluminum') return finish;
    return 'brass';
  }

  /** Published GPI by model/spine (TitleX product pages). */
  const SHAFT_GPI = {
    tx5: { 250: 11.2, 300: 10.2, 350: 9.2, 400: 8.4 },
    tx23: { 250: 8.1, 300: 7.8, 350: 7.2, 400: 6.8 },
    machxv: { 250: 9.7, 300: 8.8, 350: 7.9, 400: 7.0 },
  };
  const INSERT_GRAINS = { brass: 75, stainless: 50, aluminum: 20 };
  const NOCK_GRAINS = 10;
  const VANE_PRODUCTS = {
    hybrid: { id: 'hybrid', name: 'AAE Hybrid 26', grains: 8.5, lengthIn: 2.7, heightIn: 0.5 },
    alpha: { id: 'alpha', name: 'Bohning Alpha', grains: 8.5, lengthIn: 2.8, heightIn: 0.45 },
  };
  const VANE_PRODUCT = VANE_PRODUCTS.hybrid;
  const VANE_GRAINS = { hybrid: VANE_PRODUCT.grains, alpha: VANE_PRODUCTS.alpha.grains, standard: 5.5 };
  const ALPHA_VANE_COLOR_NAMES = [
    'White',
    'Olive',
    'Tan',
    'Neon Green',
    'Yellow',
    'Navy',
    'Dark Green',
    'Orange',
    'Red',
    'Brown',
    'Hot Pink',
    'Black',
    'Purple',
  ];
  const SHAFT_STRAIGHTNESS = '.001';
  const SPINE_KEYS = ['250', '300', '350', '400'];
  const WRAP_GRAINS = 10;
  const FULL_SHAFT_INCHES = 32;

  function normalizeFieldPointGrain(grain) {
    const n = parseInt(grain, 10);
    if (n === 200) return 200;
    if (n === 150) return 150;
    if (n === 125) return 125;
    if (n === 120) return 120;
    return 100;
  }

  /** 150 gr exists only for stainless; black/none fall back to 125. Glue-in is 120/200. */
  function fieldPointGrainForFinish(grain, finish) {
    if (finish === 'gluein') return normalizeGlueInGrain(grain);
    const g = normalizeFieldPointGrain(grain);
    if (g === 120 || g === 200) return 100;
    if (finish !== 'stainless' && g === 150) return 125;
    return g;
  }

  function currentPointGrain(viewerState) {
    if (isTargetModel(viewerState.model)) {
      return normalizeGlueInGrain(viewerState.fieldPointGrain);
    }
    return fieldPointGrainForFinish(
      viewerState.fieldPointGrain,
      viewerState.fieldPointFinish || 'stainless'
    );
  }

  function parseGpiFromText(text) {
    const m = String(text || '').match(/gpi\s*[:\s]*([\d.]+)/i);
    return m ? parseFloat(m[1]) : NaN;
  }

  function formatSpecLabel(name, spec) {
    if (spec == null || spec === '') return String(name);
    return String(name) + ' · ' + spec;
  }

  function spineLabel(model, spine) {
    const table = SHAFT_GPI[model] || SHAFT_GPI.machxv;
    const gpi = table[String(spine)];
    if (gpi == null) return String(spine);
    return formatSpecLabel(spine, Number(gpi).toFixed(1) + ' GPI');
  }

  function spineLabelsFor(model) {
    const labels = {};
    SPINE_KEYS.forEach(function (spine) {
      labels[spine] = spineLabel(model, spine);
    });
    return labels;
  }

  function insertLabels() {
    return {
      off: 'None',
      brass: formatSpecLabel('Brass', INSERT_GRAINS.brass + ' gr'),
      stainless: formatSpecLabel('Stainless', INSERT_GRAINS.stainless + ' gr'),
      aluminum: formatSpecLabel('Black aluminum', INSERT_GRAINS.aluminum + ' gr'),
    };
  }

  function vaneProductByStyle(style) {
    return style === 'alpha' ? VANE_PRODUCTS.alpha : VANE_PRODUCTS.hybrid;
  }

  function vaneSpecLabel(style) {
    const product = vaneProductByStyle(style);
    return product.name + ' · ' + product.lengthIn + ' in · ' + product.grains + ' gr';
  }

  function vaneColorNames(style) {
    return style === 'alpha' ? ALPHA_VANE_COLOR_NAMES : VANE_COLOR_NAMES;
  }

  function vaneProductLabel() {
    return vaneSpecLabel('hybrid');
  }

  function vaneGrainsEach(style) {
    if (style === 'standard') return VANE_GRAINS.standard;
    return vaneProductByStyle(style).grains;
  }

  function shaftGpi(viewerState, catalog) {
    const model = viewerState.model || 'tx5';
    const spine = String(viewerState.spine || '250');
    const table = SHAFT_GPI[model] || SHAFT_GPI.tx5;
    let gpi = table[spine] || table['300'] || 9;
    const product = catalog && catalog.shafts && catalog.shafts[model];
    if (product && product.variants) {
      for (let i = 0; i < product.variants.length; i++) {
        const v = product.variants[i];
        const blob = [v.title, v.option1, v.option2, v.option3].join(' ');
        if (blob.indexOf(spine) === -1) continue;
        const parsed = parseGpiFromText(blob);
        if (!Number.isNaN(parsed) && parsed > 0) {
          gpi = parsed;
          break;
        }
      }
    }
    return gpi;
  }

  function finishedLengthInches(viewerState) {
    const inches = Number(viewerState.cutLength);
    if (Number.isNaN(inches) || inches >= FULL_SHAFT_INCHES) return FULL_SHAFT_INCHES;
    return Math.max(20, Math.min(FULL_SHAFT_INCHES, inches));
  }

  /**
   * Estimated finished-arrow mass and AMO FOC (balance from nock throat).
   * Component stations are typical hunting/target layouts, not a scale reading.
   */
  function estimateBuildBallistics(viewerState, catalog) {
    const L = finishedLengthInches(viewerState);
    const gpi = shaftGpi(viewerState, catalog);
    const shaftGr = gpi * L;
    const target = isTargetModel(viewerState.model);
    const insertOn = !target && !!viewerState.insert;
    const insertFinish = normalizeInsertFinish(viewerState.insertFinish);
    const insertGr = insertOn ? INSERT_GRAINS[insertFinish] || 75 : 0;
    const pointOn =
      viewerState.fieldPoint !== false && (target || viewerState.fieldPointFinish !== 'none');
    let pointGr = 0;
    if (pointOn) {
      if (target) pointGr = normalizeGlueInGrain(viewerState.fieldPointGrain);
      else pointGr = fieldPointGrainForFinish(viewerState.fieldPointGrain, viewerState.fieldPointFinish);
    }
    const vaneCount = viewerState.fletchCount >= 4 ? 4 : 3;
    const vaneEach = vaneGrainsEach(viewerState.vaneStyle);
    const vaneGr = vaneEach * vaneCount;
    const wrapOn = viewerState.wrapKey && viewerState.wrapKey !== 'none';
    const wrapGr = wrapOn ? WRAP_GRAINS : 0;
    const nockGr = NOCK_GRAINS;

    const parts = [
      { name: 'Shaft', detail: gpi + ' GPI × ' + L + '"', gr: shaftGr, x: L / 2 },
      { name: 'Nock', detail: 'standard (lighted is cart add-on only)', gr: nockGr, x: 0.3 },
      {
        name: 'Vanes',
        detail:
          vaneCount +
          ' × ' +
          vaneEach +
          ' gr (' +
          vaneProductByStyle(viewerState.vaneStyle).name +
          ')',
        gr: vaneGr,
        x: 2.3,
      },
      { name: 'Wrap', detail: wrapOn ? '7" vinyl (assumed)' : 'none', gr: wrapGr, x: 4.2 },
      {
        name: 'Insert',
        detail: insertOn ? insertFinish + ' (' + insertGr + ' gr)' : 'none',
        gr: insertGr,
        x: Math.max(0, L - 0.45),
      },
      {
        name: target ? 'Glue-in point' : 'Field point',
        detail: pointOn ? pointGr + ' gr' : 'none',
        gr: pointGr,
        x: L + 0.35,
      },
    ];
    let totalGr = 0;
    let moment = 0;
    parts.forEach(function (p) {
      if (p.gr <= 0) return;
      totalGr += p.gr;
      moment += p.gr * p.x;
    });
    const balance = totalGr > 0 ? moment / totalGr : L / 2;
    const mid = L / 2;
    const focPercent = L > 0 ? (100 * (balance - mid)) / L : 0;
    return {
      totalGr: Math.round(totalGr),
      totalGrExact: totalGr,
      focPercent: Math.round(focPercent * 10) / 10,
      focExact: focPercent,
      includesPoint: pointOn && pointGr > 0,
      lengthIn: L,
      gpi: gpi,
      mid: mid,
      balance: balance,
      moment: moment,
      parts: parts,
    };
  }

  function logBuildBallistics(stats, viewerState) {
    const s = viewerState || {};
    const model = s.model || 'tx5';
    const spine = s.spine || '';
    const vaneCount = s.fletchCount >= 4 ? 4 : 3;
    const vaneStyle = s.vaneStyle || 'hybrid';
    const vaneEach = vaneGrainsEach(vaneStyle);
    const helical =
      s.helical === 'left' ? 'left' : s.helical === 'none' ? 'straight' : 'right';
    const target = isTargetModel(s.model);
    let insertSummary = 'none';
    if (target) insertSummary = 'none (glue-in)';
    else if (s.insert) insertSummary = s.insertFinish || 'brass';
    let fieldPointSummary = 'none';
    if (stats.includesPoint) {
      if (target) {
        fieldPointSummary = normalizeGlueInGrain(s.fieldPointGrain) + ' gr glue-in';
      } else {
        fieldPointSummary =
          (s.fieldPointFinish || 'stainless') + ' ' + (s.fieldPointGrain || 100) + ' gr';
      }
    }
    const fmt = function (n, d) {
      return (Math.round(n * Math.pow(10, d)) / Math.pow(10, d)).toFixed(d);
    };

    console.group(
      '[Arrow builder math] ' +
        stats.totalGr +
        ' GR  ·  FOC ' +
        stats.focPercent.toFixed(1) +
        '%  ·  ' +
        model +
        ' ' +
        spine
    );

    console.log('Build', {
      model: model,
      spine: spine,
      gpi: stats.gpi,
      cutLengthIn: stats.lengthIn,
      insert: insertSummary,
      fieldPoint: fieldPointSummary,
      vanes: {
        style: vaneStyle,
        count: vaneCount,
        helical: helical,
        primary: s.vanePrimary || '',
        secondary: s.vaneSecondary || '',
        assumedGrEach: vaneEach,
      },
      wrap: s.wrapKey && s.wrapKey !== 'none' ? s.wrapKey : 'none',
      nock: 'standard (' + NOCK_GRAINS + ' gr) — lighted nocks not in weight/FOC',
    });

    console.group('1) Total weight');
    console.log('Assumed component grains (not a scale):');
    console.log('  Shaft:     ' + stats.gpi + ' GPI × ' + stats.lengthIn + '" = ' + fmt(stats.gpi * stats.lengthIn, 2) + ' gr');
    console.log(
      '  Vanes:     ' +
        vaneCount +
        ' fletch × ' +
        vaneEach +
        ' gr  [' +
        vaneStyle +
        ', ' +
        helical +
        ' helical]\n' +
        '             colors: ' +
        (s.vanePrimary || '?') +
        (vaneCount >= 3 && s.vaneSecondary ? ' + cock ' + s.vaneSecondary : '') +
        '\n' +
        '             lookup: ' +
        vaneProductByStyle(vaneStyle).name +
        '=' +
        vaneProductByStyle(vaneStyle).grains +
        ' gr, standard=' +
        VANE_GRAINS.standard +
        ' gr\n' +
        '             = ' +
        fmt(vaneEach * vaneCount, 2) +
        ' gr   (color does not change weight)'
    );
    console.log(
      '  Nock:      standard ' +
        NOCK_GRAINS +
        ' gr  (lighted nock is a cart add-on, not in this total)'
    );
    console.log(
      '  Wrap:      ' +
        (s.wrapKey && s.wrapKey !== 'none'
          ? WRAP_GRAINS + ' gr assumed for ~7" vinyl (' + s.wrapKey + ')'
          : '0 gr (none)')
    );
    console.log(
      '  Insert:    ' +
        (s.insert
          ? (s.insertFinish || 'brass') +
            ' = ' +
            (INSERT_GRAINS[normalizeInsertFinish(s.insertFinish)] || 75) +
            ' gr  (brass HIT 75 / stainless half-out 50 / aluminum 20)'
          : '0 gr (none)')
    );
    let pointLog = '0 gr (none)';
    if (stats.includesPoint) {
      if (target) {
        pointLog = normalizeGlueInGrain(s.fieldPointGrain) + ' gr glue-in';
      } else {
        pointLog =
          fieldPointGrainForFinish(s.fieldPointGrain, s.fieldPointFinish || 'stainless') +
          ' gr field point';
      }
    }
    console.log('  Point:     ' + pointLog);
    const addLine = (stats.parts || [])
      .filter(function (p) {
        return p.gr > 0;
      })
      .map(function (p) {
        return fmt(p.gr, 2);
      })
      .join(' + ');
    console.log('  Sum:       ' + addLine + ' = ' + fmt(stats.totalGrExact, 2) + ' gr');
    console.log('  Display:   round(' + fmt(stats.totalGrExact, 2) + ') = ' + stats.totalGr + ' GR');
    console.groupEnd();

    console.group('2) Estimated FOC (AMO)');
    console.log('Stations are inches from nock throat toward the point.');
    console.log('  Vanes sit together at 2.3" (rear of shaft). Helical / 3 vs 4 fletch changes mass only, not vane station.');
    console.log(
      '  Formula: FOC% = 100 × (balance − L/2) / L\n' +
        '  L = ' +
        stats.lengthIn +
        '"   L/2 = ' +
        fmt(stats.mid, 3) +
        '"'
    );
    (stats.parts || []).forEach(function (p) {
      if (p.gr <= 0) {
        console.log('  ' + p.name + ': skipped (' + p.detail + ')');
        return;
      }
      console.log(
        '  ' +
          p.name +
          ': ' +
          fmt(p.gr, 2) +
          ' gr × ' +
          fmt(p.x, 2) +
          '" = ' +
          fmt(p.gr * p.x, 2) +
          ' gr·in   [' +
          p.detail +
          ']'
      );
    });
    console.log(
      '  balance = Σ(moment) / Σ(grains) = ' +
        fmt(stats.moment, 2) +
        ' / ' +
        fmt(stats.totalGrExact, 2) +
        ' = ' +
        fmt(stats.balance, 3) +
        '" from nock'
    );
    console.log(
      '  FOC = 100 × (' +
        fmt(stats.balance, 3) +
        ' − ' +
        fmt(stats.mid, 3) +
        ') / ' +
        stats.lengthIn +
        ' = ' +
        fmt(stats.focExact, 3) +
        '%  →  display ' +
        stats.focPercent.toFixed(1) +
        '%'
    );
    console.groupEnd();

    console.table(
      (stats.parts || []).map(function (p) {
        const used = p.gr > 0;
        return {
          part: p.name,
          detail: p.detail,
          grains: used ? Math.round(p.gr * 100) / 100 : 0,
          station_in_from_nock: used ? Math.round(p.x * 100) / 100 : '—',
          moment_gr_in: used ? Math.round(p.gr * p.x * 100) / 100 : 0,
        };
      })
    );
    console.groupEnd();
  }

  function colorToHex(name) {
    if (!name) return '#888888';
    const key = String(name).trim().toLowerCase();
    if (COLOR_MAP[key]) return COLOR_MAP[key];
    if (/^#[0-9a-f]{3,8}$/i.test(key)) return key;
    return '#888888';
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /** Fill remaining viewport under theme chrome (announcements + header). */
  function syncViewportFill(root) {
    if (!root) return;
    const y = Math.max(0, Math.round(root.getBoundingClientRect().top + window.scrollY));
    document.documentElement.style.setProperty('--apg-doc-top', y + 'px');
  }

  /** Stop Dawn sticky-header scroll hide/reveal / logo shrink on this page. */
  function disableStickyHeader() {
    const sticky = document.querySelector('sticky-header');
    const header = document.querySelector('.section-header');
    if (header) {
      header.classList.remove(
        'shopify-section-header-sticky',
        'shopify-section-header-hidden',
        'animate',
        'scrolled-past-header'
      );
    }
    if (!sticky) return;
    try {
      if (sticky.onScrollHandler) {
        window.removeEventListener('scroll', sticky.onScrollHandler, false);
      }
      if (typeof sticky.disconnectedCallback === 'function') {
        sticky.disconnectedCallback();
      }
      // Neutralize future scroll callbacks if the element stays in the DOM
      sticky.onScroll = function () {};
      sticky.hide = function () {};
      sticky.reveal = function () {};
      sticky.reset = function () {};
      if (sticky.headerIsAlwaysSticky) sticky.headerIsAlwaysSticky = false;
    } catch (e) {
      console.warn('[Arrow Playground] sticky header disable failed', e);
    }
  }

  function loadPersisted() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || data.v !== STORAGE_VERSION || !data.state) return null;
      return data;
    } catch (e) {
      console.warn('[Arrow Playground] load failed', e);
      return null;
    }
  }

  function savePersisted(state, qty) {
    try {
      const payload = {
        v: STORAGE_VERSION,
        savedAt: Date.now(),
        qty: qty,
        state: {
          model: state.model,
          spine: state.spine,
          wrapKey: state.wrapKey,
          fletchCount: state.fletchCount,
          vanePrimary: state.vanePrimary,
          vaneSecondary: state.vaneSecondary,
          vaneStyle: state.vaneStyle,
          nockColor: state.nockColor,
          lightedNocks: state.lightedNocks,
          lightedNockColor: state.lightedNockColor,
          insert: state.insert,
          insertFinish: state.insertFinish,
          insertAluminumOd: state.insertAluminumOd,
          fieldPoint: state.fieldPoint,
          fieldPointFinish: state.fieldPointFinish,
          fieldPointGrain: state.fieldPointGrain,
          cutLength: state.cutLength,
          helical: state.helical,
          laborMode: 'titlex',
        },
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn('[Arrow Playground] save failed', e);
    }
  }

  function parseCatalog(root) {
    const el = root.querySelector('[data-arrow-playground-catalog]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      console.warn('[Arrow Playground] catalog parse failed', e);
      return null;
    }
  }

  function formatMoney(cents, moneyFormat) {
    const n = Math.max(0, Math.round(Number(cents) || 0));
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      try {
        return Shopify.formatMoney(n, moneyFormat || undefined);
      } catch (e) {
        /* fall through */
      }
    }
    return '$' + (n / 100).toFixed(2);
  }

  function withMarkup(label, priceCents) {
    if (!priceCents) return label;
    return label + ' (+' + formatMoney(priceCents) + ')';
  }

  function normOpt(s) {
    return String(s || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  function variantOptions(v) {
    return [v.option1, v.option2, v.option3].filter(Boolean);
  }

  function detectArrowsPerUnit(v, product) {
    const parts = variantOptions(v)
      .concat([v && v.title, product && product.title, product && product.handle])
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    // Prefer explicit 12 / dozen before 6; never treat 6.5mm as a 6-pack.
    if (/\b12\s*(pk|pcs|pc|arrows?|shafts?)\b/.test(parts) || /\bdozen\b/.test(parts) || /\(12\s*shafts?\)/.test(parts)) {
      return 12;
    }
    if (/(^|[^0-9.])6\s*(pk|pcs|pc|arrows?|shafts?)\b/.test(parts) || /\(6\s*shafts?\)/.test(parts)) {
      return 6;
    }
    return null;
  }

  function isPack(v, pack, product) {
    const size = detectArrowsPerUnit(v, product);
    if (size != null) return size === pack;
    const needle = pack === 6 ? '6pk' : '12pk';
    return variantOptions(v).some(function (o) {
      const n = normOpt(o);
      return n === needle || (n.indexOf(needle) >= 0 && n.indexOf('6.5') < 0);
    });
  }

  function hasSpine(v, spine) {
    const s = String(spine || '').trim();
    if (!s) return false;
    return variantOptions(v).some(function (o) {
      const t = String(o).trim();
      return t === s || t.indexOf(s) >= 0;
    });
  }

  function hasGlueInPoint(v, grain) {
    const g = String(normalizeGlueInGrain(grain));
    const blob = variantOptions(v).concat([v.title]).join(' ').toLowerCase();
    if (new RegExp('(?:^|[^0-9])' + g + '\\s*(?:grain|gr)\\b').test(blob)) return true;
    return g === '200' && /break\s*-?\s*off/.test(blob);
  }

  function pickVariantFromProduct(product, matcher) {
    if (!product || !product.variants) return null;
    const available = product.variants.filter(function (v) {
      return v.available !== false;
    });
    const pool = available.length ? available : product.variants;
    const matched = pool.filter(matcher);
    return matched[0] || null;
  }

  /**
   * Resolve dozen + half-dozen SKUs.
   * Never fall back to using the dozen SKU as the half-dozen (that doubles shafts).
   */
  function pickPackPair(product, matcher, product6) {
    if (!product || !product.variants) return null;
    const available = product.variants.filter(function (v) {
      return v.available !== false;
    });
    const pool = available.length ? available : product.variants;
    const matched = pool.filter(matcher);
    if (!matched.length) return null;

    let v12 = matched.find(function (v) {
      return isPack(v, 12, product);
    });
    if (!v12) {
      // Product itself may be a fixed dozen (title) with no pack option
      const productUnit = detectArrowsPerUnit(matched[0], product);
      if (productUnit === 12 || productUnit == null) v12 = matched[0];
    }

    let v6 = null;
    let product6Used = null;
    if (product6 && product6.variants) {
      v6 = pickVariantFromProduct(product6, matcher);
      product6Used = product6;
    }
    if (!v6) {
      v6 = matched.find(function (v) {
        return isPack(v, 6, product);
      });
      if (v6) product6Used = product;
    }
    if (!v6) {
      v6 = pool.find(function (v) {
        return matcher(v) && isPack(v, 6, product);
      });
      if (v6) product6Used = product;
    }

    const unit12 = v12 ? detectArrowsPerUnit(v12, product) || 12 : null;
    const unit6 = v6 ? detectArrowsPerUnit(v6, product6Used || product) || 6 : null;

    // If we only found a 6-unit SKU living on the dozen product slot, treat as 6-only.
    if (v12 && unit12 === 6 && !v6) {
      return {
        id12: null,
        price12: 0,
        unit12: null,
        id6: v12.id,
        price6: v12.price,
        unit6: 6,
        title: product.title || '',
        label: v12.title || '',
      };
    }

    return {
      id12: v12 ? v12.id : null,
      price12: v12 ? v12.price : 0,
      unit12: v12 ? unit12 || 12 : null,
      id6: v6 ? v6.id : null,
      price6: v6 ? v6.price : 0,
      unit6: v6 ? unit6 || 6 : null,
      title: product.title || '',
      label: v12 ? v12.title : v6 ? v6.title : '',
    };
  }

  function isExactSizeToken(opt, sizeToken) {
    const o = normOpt(opt);
    const t = normOpt(sizeToken);
    if (!o || !t) return false;
    if (o === t) return true;
    if (o.indexOf(t) === 0 && o.length > t.length) {
      const next = o.charAt(t.length);
      return next !== '.' && !/[0-9]/.test(next);
    }
    return false;
  }

  function variantHasSize(v, sizeToken) {
    return variantOptions(v).some(function (o) {
      return isExactSizeToken(o, sizeToken);
    });
  }

  /** Bowtique wraps: TitleX .204 shafts use 5MM (not 6.5MM / 5.5MM). TX-23 → 23 DIA. */
  function wrapSizeTokenForModel(modelKey) {
    if (modelKey === 'tx23') return '23dia';
    return '5mm';
  }

  function priceKeyFromCents(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n) || n <= 0) return '';
    return (n / 100).toFixed(2);
  }

  /** Flow sync: option/SKU = Bowtique $ amount; install price may be Bowtique + $5. */
  function installVariantMatches(v, bowtiqueCents) {
    if (!v) return false;
    const key = priceKeyFromCents(bowtiqueCents);
    if (!key) return false;
    const sku = String(v.sku || '').toUpperCase();
    const opt = String(v.option1 || '').trim();
    const title = String(v.title || '').trim();
    if (sku === 'TX-WRAP-' + key || opt === key || title === key) return true;
    const price = Number(v.price);
    if (price === Number(bowtiqueCents) + 500) return true;
    if (price === Number(bowtiqueCents)) return true;
    return false;
  }

  function pickInstallVariant(catalog, bowtiqueCents) {
    const products = [catalog && catalog.wrapInstall, catalog && catalog.wrapInstall6].filter(Boolean);
    for (let i = 0; i < products.length; i++) {
      const variants = products[i].variants || [];
      const hit = variants.find(function (v) {
        return installVariantMatches(v, bowtiqueCents);
      });
      if (hit) return hit;
    }
    return null;
  }

  /** Manifest / state keys use `handle` or `handle__color-slug`. */
  function wrapBaseHandle(wrapKey) {
    const k = String(wrapKey || '');
    const i = k.indexOf('__');
    return i >= 0 ? k.slice(0, i) : k;
  }

  function wrapSearchNormalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[\/_\-(),]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  const HIDDEN_WRAP_HANDLES = {
    'he-s-hunting-crew-neck-sweatshirt': true,
    'arrow-wrap-pad-pre-order-limited-edition': true,
  };

  function isHiddenWrapHandle(handle) {
    const raw = String(handle || '');
    if (!raw) return false;
    if (HIDDEN_WRAP_HANDLES[raw] || HIDDEN_WRAP_HANDLES[wrapBaseHandle(raw)]) {
      return true;
    }
    const hay = raw.toLowerCase().replace(/-/g, ' ');
    return hay.indexOf('wrap pad') >= 0 || hay.indexOf('arrow pad') >= 0;
  }

  /**
   * Shopify collection handles often omit/add `-arrow-wraps` vs the texture manifest.
   * Only add or remove a wraps suffix — never remap via a short shared stem
   * (e.g. 5mm-arrow-wraps must NOT match 5mm-wraps / Western).
   */
  function matchWrapManifestKey(handle, bases) {
    const h = String(handle || '').trim();
    if (!h || !bases || !bases.length) return '';
    if (bases.indexOf(h) >= 0) return h;

    const withSuffix = [h + '-arrow-wraps', h + '-arrow-wrap', h + '-wraps', h + '-wrap'];
    for (let i = 0; i < withSuffix.length; i++) {
      if (bases.indexOf(withSuffix[i]) >= 0) return withSuffix[i];
    }

    const suffixes = ['-arrow-wraps', '-arrow-wrap', '-wraps', '-wrap'];
    for (let i = 0; i < suffixes.length; i++) {
      const suffix = suffixes[i];
      if (h.length > suffix.length && h.slice(-suffix.length) === suffix) {
        const stripped = h.slice(0, -suffix.length);
        if (stripped && bases.indexOf(stripped) >= 0) return stripped;
      }
    }
    return '';
  }

  /** Keep in sync with scripts/wraps/wrap-brand-key.mjs */
  var WRAP_BRAND_PRIORITY = ['bowtique', 'titlex', 'deer-hunt-co'];

  function wrapBrandKey(vendor) {
    const v = String(vendor || '').trim();
    if (!v) return '';
    if (/bowtique/i.test(v)) return 'bowtique';
    if (/titlex/i.test(v)) return 'titlex';
    if (/deer\s*(lord\s*)?hunt/i.test(v)) return 'deer-hunt-co';
    return (
      v
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || ''
    );
  }

  function wrapBrandLabel(brandKey, vendor) {
    if (brandKey === 'bowtique') return 'Bowtique';
    if (brandKey === 'titlex') return 'TitleX';
    if (brandKey === 'deer-hunt-co') return 'Deer Lord Hunt Co';
    const raw = String(vendor || '').trim();
    return raw || brandKey || '';
  }

  function collectWrapBrandChips(wrapKeys, wrapBrands) {
    const seen = {};
    const others = [];
    (wrapKeys || []).forEach(function (key) {
      if (!key || key === 'none') return;
      const info = (wrapBrands && wrapBrands[key]) || {};
      const bk = String(info.brandKey || wrapBrandKey(info.vendor) || '');
      if (!bk || seen[bk]) return;
      seen[bk] = info.label || wrapBrandLabel(bk, info.vendor);
      if (WRAP_BRAND_PRIORITY.indexOf(bk) < 0) others.push(bk);
    });
    others.sort(function (a, b) {
      return String(seen[a]).localeCompare(String(seen[b]));
    });
    const order = WRAP_BRAND_PRIORITY.filter(function (k) {
      return !!seen[k];
    }).concat(others);
    return [{ key: 'all', label: 'All' }].concat(
      order.map(function (k) {
        return { key: k, label: seen[k] };
      })
    );
  }

  function wrapColorSlug(wrapKey) {
    const k = String(wrapKey || '');
    const i = k.indexOf('__');
    return i >= 0 ? k.slice(i + 2) : '';
  }

  function wrapColorDisplayName(wrapKey, entry) {
    if (entry && entry.color) return String(entry.color);
    const slug = wrapColorSlug(wrapKey);
    if (!slug) return '';
    return slug
      .split('-')
      .filter(Boolean)
      .map(function (part) {
        return part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join(' ');
  }

  function defaultWrapColorKey(baseKey, colorList) {
    if (!colorList || !colorList.length) return baseKey;
    for (let i = 0; i < colorList.length; i++) {
      const c = colorList[i];
      if (c.slug === 'black' || String(c.color || '').toLowerCase() === 'black') {
        return c.key;
      }
    }
    return colorList[0].key;
  }

  /** Keep in sync with scripts/playground/option-availability.mjs */
  function matchingVariants(variants, predicate) {
    if (!Array.isArray(variants)) return [];
    if (!predicate) return variants.slice();
    return variants.filter(predicate);
  }

  function isSoldOut(variants, predicate, missingMeansSoldOut) {
    if (!Array.isArray(variants) || !variants.length) return false;
    const matched = matchingVariants(variants, predicate);
    if (!matched.length) return !!missingMeansSoldOut;
    return matched.every(function (v) {
      return v && v.available === false;
    });
  }

  function soldOutLabel(name) {
    const n = String(name || '').trim();
    return n ? n + ' · Out of stock' : 'Out of stock';
  }

  function variantHasColor(v, colorName) {
    if (!colorName) return true;
    const want = normOpt(colorName);
    if (!want) return true;
    const o1 = normOpt(v.option1 || '');
    if (o1 === want || (o1 && o1.indexOf(want) >= 0)) return true;
    return variantOptions(v).some(function (o) {
      const n = normOpt(o);
      return n === want || (n && n.indexOf(want) >= 0);
    });
  }

  function pickWrapPair(product, modelKey, colorName) {
    if (!product) return null;
    const sizeToken = wrapSizeTokenForModel(modelKey);
    function matchSizeColor(v, sizeTok) {
      if (!variantHasSize(v, sizeTok)) return false;
      return variantHasColor(v, colorName);
    }
    let pair = pickPackPair(product, function (v) {
      return matchSizeColor(v, sizeToken);
    });
    if (pair && (pair.id12 || pair.id6)) return pair;
    if (modelKey === 'tx23') {
      pair = pickPackPair(product, function (v) {
        const blob = normOpt(variantOptions(v).join(' '));
        const sizeOk =
          blob.indexOf('23dia') >= 0 || blob.indexOf('xbow') >= 0 || blob === '23';
        return sizeOk && variantHasColor(v, colorName);
      });
      if (pair && (pair.id12 || pair.id6)) return pair;
    }
    pair = pickPackPair(product, function (v) {
      return matchSizeColor(v, '5mm');
    });
    return pair;
  }

  function pickFirstPackPair(product, product6) {
    return pickPackPair(
      product,
      function () {
        return true;
      },
      product6
    );
  }

  function pickSpinePair(product, spine, product6, glueGrain) {
    function matcher(v) {
      if (!hasSpine(v, spine)) return false;
      if (glueGrain == null) return true;
      return hasGlueInPoint(v, glueGrain);
    }
    let pair = pickPackPair(product, matcher, product6);
    if ((!pair || (!pair.id12 && !pair.id6)) && glueGrain != null) {
      pair = pickPackPair(
        product,
        function (v) {
          return hasSpine(v, spine);
        },
        product6
      );
    }
    return pair;
  }

  function pickLightedPair(product, nockColor) {
    if (!product || !product.variants) return null;
    const available = product.variants.filter(function (v) {
      return v.available !== false;
    });
    const pool = available.length ? available : product.variants;
    const want = normOpt(nockColor);
    let v =
      want &&
      pool.find(function (x) {
        const blob = normOpt(variantOptions(x).concat([x.title]).join(' '));
        return blob === want || blob.indexOf(want) >= 0;
      });
    if (!v) v = pool[0];
    if (!v) return null;
    const unit = detectArrowsPerUnit(v, product) || 6;
    return {
      id12: null,
      price12: 0,
      unit12: null,
      id6: v.id,
      price6: v.price,
      unit6: unit,
      title: product.title || '',
      label: v.title || '',
    };
  }

  function colorOptClose(a, b) {
    const x = normOpt(a);
    const y = normOpt(b);
    if (!x || !y) return false;
    if (x === y) return true;
    if (x.indexOf(y) >= 0 || y.indexOf(x) >= 0) return true;
    if (x.indexOf('gold') >= 0 && y.indexOf('gold') >= 0) return true;
    return false;
  }

  /** Builder color -> Bohning Alpha fletch option. Product has one Color option. */
  const ALPHA_FLETCH_COLOR = {
    white: 'white',
    black: 'black',
    yellow: 'yellow',
    orange: 'orange',
    red: 'red',
    brown: 'brown',
    purple: 'purple',
    hotpink: 'pink',
    pink: 'pink',
    olive: 'odgreen',
    darkgreen: 'odgreen',
    neongreen: 'green',
    green: 'green',
    tan: 'sand',
    sand: 'sand',
    navy: 'blue',
    blue: 'blue',
  };

  function pickAlphaFletchingLabor(product, primary) {
    if (!product || !product.variants || !product.variants.length) return null;
    const available = product.variants.filter(function (v) {
      return v.available !== false;
    });
    const base = available.length ? available : product.variants;
    const want = ALPHA_FLETCH_COLOR[normOpt(primary)] || normOpt(primary);
    const v =
      base.find(function (x) {
        return normOpt(x.option1 || x.title) === want;
      }) ||
      base.find(function (x) {
        return colorOptClose(x.option1 || x.title, want);
      }) ||
      base.find(function (x) {
        return normOpt(x.option1 || x.title) === 'yellow';
      }) ||
      base[0];
    if (!v || !v.id) return null;
    return {
      id: v.id,
      price: v.price,
      label: v.title || '',
      title: product.title || '',
      withWrapLabor: false,
    };
  }

  /** Custom Fletching per Dozen — $36 hybrid / $48 hybrid+wrap labor. */
  function pickFletchingLabor(product, primary, secondary, hasWrap) {
    if (!product || !product.variants || !product.variants.length) return null;
    const available = product.variants.filter(function (v) {
      return v.available !== false;
    });
    const base = available.length ? available : product.variants;

    function materialOk(v) {
      const o1 = normOpt(v.option1 || v.title || '');
      const wrapish = o1.indexOf('wrap') >= 0;
      return hasWrap ? wrapish : !wrapish;
    }

    function colorScore(v) {
      const o2 = v.option2 || '';
      const o3 = v.option3 || '';
      let s = 0;
      if (colorOptClose(o2, primary)) s += 2;
      if (colorOptClose(o3, secondary)) s += 2;
      return s;
    }

    const matPool = base.filter(materialOk);
    const search = (matPool.length ? matPool : base).slice().sort(function (a, b) {
      return colorScore(b) - colorScore(a);
    });
    const v =
      search.find(function (x) {
        return colorScore(x) >= 4;
      }) ||
      search.find(function (x) {
        return colorScore(x) >= 2;
      }) ||
      search[0];
    if (!v) return null;
    return {
      id: v.id,
      price: v.price,
      label: v.title || '',
      title: product.title || '',
      withWrapLabor: !!hasWrap,
    };
  }

  function pickWrapInstallLabor(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    const available = product.variants.filter(function (v) {
      return v.available !== false && v.id;
    });
    const v = available[0] || product.variants[0];
    if (!v || !v.id) return null;
    return {
      id: v.id,
      price: v.price || 0,
      label: v.title || product.title || 'Wrap install labor',
    };
  }

  function normalizeLaborMode(mode, hasWrap) {
    const m =
      mode === 'wrap_only' || mode === 'diy' || mode === 'none' ? mode : 'titlex';
    if (!hasWrap && m === 'wrap_only') return 'titlex';
    return m;
  }

  function pickAluminumHalfout(product, od) {
    if (!product || !product.variants || !product.variants.length) return null;
    const available = product.variants.filter(function (v) {
      return v.available !== false;
    });
    const pool = available.length ? available : product.variants;
    const want = normOpt(od || '6.8mm');
    let v =
      want &&
      pool.find(function (x) {
        const blob = normOpt(variantOptions(x).concat([x.title]).join(' '));
        return blob.indexOf(want) >= 0 || blob.indexOf(want.replace('mm', '')) >= 0;
      });
    if (!v) v = pool[0];
    if (!v) return null;
    return {
      id12: v.id,
      price12: v.price,
      unit12: detectArrowsPerUnit(v, product) || 12,
      id6: null,
      price6: 0,
      unit6: null,
      label: v.title || product.title || 'Black aluminum half out',
    };
  }

  function fieldPointFinishMatch(v, finish) {
    const blob = variantOptions(v).concat([v.title]).join(' ').toLowerCase();
    if (finish === 'black') {
      return /harden|black|carbon/.test(blob);
    }
    if (finish === 'stainless') {
      return /stainless/.test(blob) || (/steel/.test(blob) && !/harden|black|carbon/.test(blob));
    }
    return false;
  }

  function fieldPointGrainMatch(v, grain) {
    const g = String(grain || '').replace(/[^0-9]/g, '');
    if (!g) return true;
    const blob = variantOptions(v).concat([v.title]).join(' ').toLowerCase();
    // Match "100 grain" / "100gr" without hitting 1100, 5.5mm 100, etc. poorly —
    // catalog uses "5mm 100 grain Field Point"
    return new RegExp('(?:^|[^0-9])' + g + '\\s*(?:grain|gr)\\b').test(blob);
  }

  function fieldPointPackScore(v, prefer12) {
    const blob = variantOptions(v).concat([v.title]).join(' ').toLowerCase();
    const is12 = /12pcs|12\s*pcs|12pk|12\s*pk/.test(blob);
    const is6 = /6pk|6\s*pk|6pcs|6\s*pcs/.test(blob);
    if (prefer12) {
      if (is12) return 2;
      if (is6) return 1;
      return 0;
    }
    if (is6) return 2;
    if (is12) return 1;
    return 0;
  }

  function matchFieldPointPool(src, finish, grain) {
    let pool = (src || []).filter(function (v) {
      return fieldPointFinishMatch(v, finish) && fieldPointGrainMatch(v, grain);
    });
    if (!pool.length) {
      pool = (src || []).filter(function (v) {
        return fieldPointFinishMatch(v, finish);
      });
    }
    return pool;
  }

  function pickFieldPoint(product, finish, grain, arrowCount) {
    if (!product || finish === 'none') return null;
    const prefer12 = (Number(arrowCount) || 12) >= 12;
    const allVariants = product.variants || [];
    const available = allVariants.filter(function (v) {
      return v.available !== false;
    });
    const basePool = available.length ? available : allVariants;

    let pool = matchFieldPointPool(basePool, finish, grain);
    if (!pool.length) {
      pool = matchFieldPointPool(allVariants, finish, grain);
    }
    if (!pool.length) return null;

    const twelves = pool
      .filter(function (v) {
        return fieldPointPackScore(v, true) === 2;
      })
      .sort(function (a, b) {
        return fieldPointPackScore(b, true) - fieldPointPackScore(a, true);
      });
    const sixes = pool
      .filter(function (v) {
        return fieldPointPackScore(v, false) === 2;
      })
      .sort(function (a, b) {
        return fieldPointPackScore(b, false) - fieldPointPackScore(a, false);
      });

    const v12 = twelves[0] || null;
    const v6 = sixes[0] || null;
    if (!v12 && !v6) {
      const fallback = pool[0];
      if (!fallback) return null;
      const unit = detectArrowsPerUnit(fallback, product);
      if (unit === 6) {
        return {
          id12: null,
          price12: 0,
          unit12: null,
          id6: fallback.id,
          price6: fallback.price,
          unit6: 6,
          label: fallback.title,
          grain: parseInt(String(grain), 10) || 100,
        };
      }
      return {
        id12: fallback.id,
        price12: fallback.price,
        unit12: unit || 12,
        id6: null,
        price6: 0,
        unit6: null,
        label: fallback.title,
        grain: parseInt(String(grain), 10) || 100,
      };
    }

    const primary = v12 || v6;
    const labelParts = [primary.option1, primary.option2, primary.option3].filter(Boolean);
    const grainNum = parseInt(String(grain), 10) || 100;
    return {
      id12: v12 ? v12.id : null,
      price12: v12 ? v12.price : 0,
      unit12: v12 ? 12 : null,
      id6: v6 ? v6.id : null,
      price6: v6 ? v6.price : 0,
      unit6: v6 ? 6 : null,
      label: labelParts.join(' – ') || primary.title,
      grain: grainNum,
    };
  }

  function newBuildId() {
    return (
      'apg_' +
      Date.now().toString(36) +
      '_' +
      Math.random().toString(36).slice(2, 8)
    );
  }

  function packCounts(qty) {
    const arrowCount = Math.max(6, Math.min(36, Math.round(Number(qty) || 12)));
    const stepped = Math.round(arrowCount / 6) * 6;
    const n = Math.max(6, Math.min(36, stepped));
    return {
      arrowCount: n,
      num12pk: Math.floor(n / 12),
      num6pk: Math.floor((n % 12) / 6),
    };
  }

  function resolveCartState(viewerState, qty, catalog) {
    if (!catalog) throw new Error('Product catalog missing. Assign shaft products in the theme editor.');
    const packs = packCounts(qty);
    const modelKey = viewerState.model || 'machxv';
    const spine = String(viewerState.spine || '250');
    const target = isTargetModel(modelKey);
    const shaftProduct = catalog.shafts && catalog.shafts[modelKey];
    const shaftProduct6 = catalog.shafts6 && catalog.shafts6[modelKey];
    const glueGrain = target ? normalizeGlueInGrain(viewerState.fieldPointGrain) : null;
    const shaft = pickSpinePair(shaftProduct, spine, shaftProduct6, glueGrain);
    if (!shaft || (!shaft.id12 && !shaft.id6)) {
      throw new Error('Could not find a shaft variant for ' + (MODEL_LABELS[modelKey] || modelKey) + ' spine ' + spine + '.');
    }
    if (packs.num12pk > 0 && !shaft.id12) {
      throw new Error('Dozen shaft product missing for ' + (MODEL_LABELS[modelKey] || modelKey) + '.');
    }
    if (packs.num6pk > 0 && !shaft.id6) {
      throw new Error(
        'Half-dozen (6 pack) shaft product missing for ' +
          (MODEL_LABELS[modelKey] || modelKey) +
          '. Assign the 6-shaft product in the theme editor.'
      );
    }

    let insertLabel = target ? 'None — glue-in point' : 'None';
    let insertPair = null;
    if (!target && viewerState.insert) {
      const finish = normalizeInsertFinish(viewerState.insertFinish);
      if (finish === 'aluminum') {
        insertLabel =
          'Black aluminum 20 gr (' + (viewerState.insertAluminumOd || '6.8mm') + ')';
        insertPair = pickAluminumHalfout(
          catalog.inserts && catalog.inserts.aluminum,
          viewerState.insertAluminumOd || '6.8mm'
        );
      } else {
        insertLabel = finish === 'stainless' ? 'Stainless steel' : 'Brass inserts';
        insertPair = pickFirstPackPair(catalog.inserts && catalog.inserts[finish]);
      }
      if (!insertPair || (!insertPair.id12 && !insertPair.id6)) {
        throw new Error('Insert product not available for ' + insertLabel + '.');
      }
    }

    let fpGrain;
    let fieldPoint = null;
    if (target) {
      fpGrain = glueGrain;
    } else {
      const fpFinish =
        viewerState.fieldPointFinish || (viewerState.fieldPoint ? 'stainless' : 'none');
      fpGrain = fieldPointGrainForFinish(viewerState.fieldPointGrain, fpFinish);
      fieldPoint = pickFieldPoint(catalog.fieldPoints, fpFinish, fpGrain, packs.arrowCount);
      if (fpFinish !== 'none' && (!fieldPoint || (!fieldPoint.id12 && !fieldPoint.id6))) {
        throw new Error(
          'Field points are unavailable for ' +
            fpFinish +
            ' / ' +
            fpGrain +
            ' gr. Try another finish or grain.'
        );
      }
    }
    const glueInLabel = glueGrain ? glueGrain + ' gr glue-in (included)' : 'None';
    let pointLabel = 'None';
    if (target) pointLabel = glueInLabel;
    else if (fieldPoint) pointLabel = fieldPoint.label;

    const wrapKey =
      !viewerState.wrapKey || viewerState.wrapKey === 'none' ? null : viewerState.wrapKey;
    let wrapsPair = null;
    let wrapsTitle = 'None';
    let wrapColorName = '';
    if (wrapKey && catalog.wraps) {
      const catalogHandle = wrapBaseHandle(wrapKey);
      const wrapProduct = catalog.wraps[catalogHandle];
      wrapColorName = wrapColorDisplayName(wrapKey);
      if (wrapProduct) {
        wrapsPair = pickWrapPair(wrapProduct, modelKey, wrapColorName || null);
        wrapsTitle = wrapProduct.title || catalogHandle;
        if (wrapColorName) wrapsTitle = wrapsTitle + ' (' + wrapColorName + ')';
        if (!wrapsPair || (!wrapsPair.id12 && !wrapsPair.id6)) {
          throw new Error(
            'Wrap size ' +
              wrapSizeTokenForModel(modelKey).toUpperCase() +
              (wrapColorName ? ' / ' + wrapColorName : '') +
              ' is not available for ' +
              (wrapProduct.title || catalogHandle)
          );
        }
      }
    }

    let wrapInstall12 = null;
    let wrapInstall6 = null;
    if (wrapsPair && (catalog.wrapInstall || catalog.wrapInstall6)) {
      wrapInstall12 = pickInstallVariant(catalog, wrapsPair.price12 || wrapsPair.price6);
      wrapInstall6 = pickInstallVariant(catalog, wrapsPair.price6 || wrapsPair.price12);
      if (!wrapInstall12 && !wrapInstall6) {
        const dollars = priceKeyFromCents(wrapsPair.price12 || wrapsPair.price6);
        throw new Error(
          'No TitleX wrap-install variant for $' +
            dollars +
            '. Wait for price-sync Flow or add Price option "' +
            dollars +
            '" on Custom Arrow Wrap Materials.'
        );
      }
    }

    let lightedPair = null;
    if (!target && viewerState.lightedNocks) {
      lightedPair = pickLightedPair(
        catalog.lightedNocks,
        viewerState.lightedNockColor || 'Bright Green'
      );
      if (!lightedPair || !lightedPair.id6) {
        throw new Error('Lighted nocks product is not available.');
      }
    }

    const wrapKeyForFletch =
      !viewerState.wrapKey || viewerState.wrapKey === 'none' ? null : viewerState.wrapKey;
    const laborMode = normalizeLaborMode(viewerState.laborMode, !!wrapKeyForFletch);
    const fletchDozenQty = Math.max(1, Math.ceil(packs.arrowCount / 12));

    let fletchLabor = null;
    let wrapLabor = null;
    if (laborMode === 'titlex') {
      const alphaVane = viewerState.vaneStyle === 'alpha';
      fletchLabor = alphaVane
        ? pickAlphaFletchingLabor(
            catalog.fletchingLaborAlpha,
            viewerState.vanePrimary || 'Yellow'
          )
        : pickFletchingLabor(
            catalog.fletchingLabor,
            viewerState.vanePrimary || 'Bright Green',
            viewerState.vaneSecondary || 'OD Green',
            !!wrapKeyForFletch
          );
      if (!fletchLabor || !fletchLabor.id) {
        throw new Error(
          alphaVane
            ? 'Bohning Alpha fletching product is not assigned or has no matching color.'
            : 'Custom fletching labor product is not assigned or no matching color variant was found.'
        );
      }
    } else if (laborMode === 'wrap_only') {
      wrapLabor = pickWrapInstallLabor(catalog.wrapInstallLabor);
      if (!wrapLabor || !wrapLabor.id) {
        throw new Error(
          'Wrap install labor product is not assigned. Set “Wrap install labor (DIY fletch)” in the theme editor.'
        );
      }
    }
    // laborMode === 'diy' | 'none' → no TitleX fletching SKU
    // none + wrap → materials only; DIY wrap attr set on ATC (or pick wrap_only for $12)
    const inches = Number(viewerState.cutLength);
    const isFullShaft = Number.isNaN(inches) || inches >= 32;
    const cutLengthLabel = isFullShaft ? 'Full Shaft (No Cut)' : inches + '"';
    let cutPair = null;
    if (!isFullShaft) {
      cutPair = pickFirstPackPair(catalog.cut);
      if (!cutPair || (!cutPair.id12 && !cutPair.id6)) {
        throw new Error('Cut length product is not assigned or unavailable.');
      }
      if (packs.num12pk > 0 && !cutPair.id12) {
        throw new Error('Cut product is missing a 12-arrow option.');
      }
      if (packs.num6pk > 0 && !cutPair.id6) {
        throw new Error('Cut product is missing a 6-arrow option.');
      }
    }

    const fletch = viewerState.fletchCount >= 4 ? '4 fletch' : '3 fletch';
    const helical =
      viewerState.helical === 'left' ? 'left' : viewerState.helical === 'none' ? 'none' : 'right';
    const ballistics = estimateBuildBallistics(viewerState, catalog);

    return {
      arrowCount: packs.arrowCount,
      num12pk: packs.num12pk,
      num6pk: packs.num6pk,
      model: MODEL_LABELS[modelKey] || modelKey,
      modelKey: modelKey,
      spine: spine,
      productTitle: shaft.title,
      baseVariantId: shaft.id12 ? String(shaft.id12) : null,
      baseVariantId6pk: shaft.id6 ? String(shaft.id6) : null,
      basePrice12: shaft.price12 || 0,
      basePrice6pk: shaft.price6 || 0,
      baseUnit12: shaft.unit12 || (shaft.id12 ? 12 : null),
      baseUnit6: shaft.unit6 || (shaft.id6 ? 6 : null),
      insert: insertLabel,
      insertVariantId: insertPair && insertPair.id12 ? String(insertPair.id12) : null,
      insertVariantId6pk: insertPair && insertPair.id6 ? String(insertPair.id6) : null,
      insertPrice: insertPair ? insertPair.price12 || 0 : 0,
      insertPrice6pk: insertPair ? insertPair.price6 || 0 : 0,
      fieldPoints: pointLabel,
      fieldPointsLabel: pointLabel,
      fieldPointsVariantId: fieldPoint && fieldPoint.id12 ? String(fieldPoint.id12) : null,
      fieldPointsVariantId6pk: fieldPoint && fieldPoint.id6 ? String(fieldPoint.id6) : null,
      fieldPointsPrice: fieldPoint ? fieldPoint.price12 || 0 : 0,
      fieldPointsPrice6pk: fieldPoint ? fieldPoint.price6 || 0 : 0,
      fieldPointGrain: fieldPoint ? fieldPoint.grain : fpGrain,
      wraps: wrapsTitle,
      wrapKey: wrapKey,
      wrapColor: wrapColorName || '',
      totalWeightGr: ballistics.totalGr,
      focPercent: ballistics.focPercent,
      weightIncludesPoint: ballistics.includesPoint,
      wrapsCollectiveId12: wrapsPair && wrapsPair.id12 ? String(wrapsPair.id12) : null,
      wrapsCollectiveId6: wrapsPair && wrapsPair.id6 ? String(wrapsPair.id6) : wrapsPair && wrapsPair.id12 ? String(wrapsPair.id12) : null,
      wrapsVariantId: wrapInstall12
        ? String(wrapInstall12.id)
        : wrapsPair && wrapsPair.id12
          ? String(wrapsPair.id12)
          : wrapsPair && wrapsPair.id6
            ? String(wrapsPair.id6)
            : null,
      wrapsVariantId6pk: wrapInstall6
        ? String(wrapInstall6.id)
        : wrapInstall12
          ? String(wrapInstall12.id)
          : wrapsPair && wrapsPair.id6
            ? String(wrapsPair.id6)
            : wrapsPair && wrapsPair.id12
              ? String(wrapsPair.id12)
              : null,
      wrapsPrice: wrapInstall12
        ? wrapInstall12.price
        : wrapsPair
          ? wrapsPair.price12 || wrapsPair.price6 || 0
          : 0,
      wrapsPrice6pk: wrapInstall6
        ? wrapInstall6.price
        : wrapInstall12
          ? wrapInstall12.price
          : wrapsPair
            ? wrapsPair.price6 || wrapsPair.price12 || 0
            : 0,
      wrapsUnitUnknown: !!(wrapsPair && !wrapsPair.unit12 && !wrapsPair.unit6),
      fletch: fletch,
      fletchVariantId: fletchLabor && fletchLabor.id ? String(fletchLabor.id) : null,
      fletchPrice: fletchLabor ? fletchLabor.price || 0 : 0,
      fletchDozenQty: fletchDozenQty,
      fletchLabel: fletchLabor ? fletchLabor.label || '' : '',
      wrapLaborVariantId: wrapLabor && wrapLabor.id ? String(wrapLabor.id) : null,
      wrapLaborPrice: wrapLabor ? wrapLabor.price || 0 : 0,
      wrapLaborDozenQty: fletchDozenQty,
      wrapLaborLabel: wrapLabor ? wrapLabor.label || '' : '',
      laborMode: laborMode,
      helical: helical,
      vaneStyle: viewerState.vaneStyle === 'alpha' ? 'alpha' : 'hybrid',
      vanePrimary: viewerState.vanePrimary || '',
      vaneSecondary: viewerState.vaneSecondary || '',
      nockColor: viewerState.nockColor || '',
      lightedNocks: viewerState.lightedNocks ? 'Yes' : 'No',
      lightedNockColor: viewerState.lightedNockColor || 'Bright Green',
      lightedNocksVariantId: null,
      lightedNocksVariantId6pk: lightedPair && lightedPair.id6 ? String(lightedPair.id6) : null,
      lightedNocksPrice: 0,
      lightedNocksPrice6pk: lightedPair ? lightedPair.price6 || 0 : 0,
      lightedNocksUnit6: lightedPair ? lightedPair.unit6 || 6 : 6,
      pinNock: 'No',
      pinNockVariantId: null,
      cutLength: cutLengthLabel,
      cutProductVariantId: cutPair && cutPair.id12 ? String(cutPair.id12) : null,
      cutProductVariantId6pk: cutPair && cutPair.id6 ? String(cutPair.id6) : null,
      cutProductPrice: cutPair ? cutPair.price12 || 0 : 0,
      cutProductPrice6pk: cutPair ? cutPair.price6 || 0 : 0,
      moneyFormat: catalog.moneyFormat,
    };
  }

  function computeTotal(state) {
    let total = 0;
    if (state.baseVariantId || state.baseVariantId6pk) {
      total += (state.basePrice12 || 0) * state.num12pk + (state.basePrice6pk || 0) * state.num6pk;
    }
    if (state.insertVariantId || state.insertVariantId6pk) {
      const insertSkuPrice = state.insertPrice || state.insertPrice6pk || 0;
      const insert6Price = state.insertPrice6pk || state.insertPrice || 0;
      if (state.insertVariantId && state.insertVariantId6pk && state.insertVariantId !== state.insertVariantId6pk) {
        total += (state.insertPrice || 0) * state.num12pk + insert6Price * state.num6pk;
      } else {
        total += insertSkuPrice * (state.num12pk + state.num6pk);
      }
    }
    if (state.wrapsVariantId || state.wrapsVariantId6pk) {
      const wrapPrice = state.wrapsPrice || state.wrapsPrice6pk || 0;
      total += wrapPrice * (state.num12pk + state.num6pk);
    }
    if (state.fletchVariantId) {
      total += (state.fletchPrice || 0) * (state.fletchDozenQty || 1);
    }
    if (state.wrapLaborVariantId) {
      total += (state.wrapLaborPrice || 0) * (state.wrapLaborDozenQty || 1);
    }
    if (state.lightedNocksVariantId6pk || state.lightedNocksVariantId) {
      const unit = state.lightedNocksUnit6 || 6;
      const packsNeeded = Math.ceil(state.arrowCount / unit);
      total += (state.lightedNocksPrice6pk || state.lightedNocksPrice || 0) * packsNeeded;
    }
    if (state.cutProductVariantId || state.cutProductVariantId6pk) {
      total += (state.cutProductPrice || 0) * state.num12pk + (state.cutProductPrice6pk || 0) * state.num6pk;
    }
    if (state.fieldPointsVariantId || state.fieldPointsVariantId6pk) {
      total +=
        (state.fieldPointsPrice || 0) * state.num12pk +
        (state.fieldPointsPrice6pk || state.fieldPointsPrice || 0) * state.num6pk;
    }
    return total;
  }

  function mergeDuplicateItems(items) {
    const byKey = {};
    items.forEach(function (item) {
      const props = item.properties || {};
      const sortedKeys = Object.keys(props).sort();
      const stableProps = {};
      sortedKeys.forEach(function (k) {
        stableProps[k] = props[k];
      });
      const key = item.id + '|' + JSON.stringify(stableProps);
      if (!byKey[key]) {
        byKey[key] = { id: item.id, quantity: 0, properties: item.properties || {} };
      }
      byKey[key].quantity += item.quantity;
    });
    return Object.keys(byKey).map(function (k) {
      return byKey[k];
    });
  }

  function buildItems(state, buildId) {
    const packCount = state.num12pk + state.num6pk;
    const packLabel = state.arrowCount === 12 ? '12pk' : state.arrowCount + ' arrows';
    const properties = {
      Quantity: String(state.arrowCount || 12),
      Model: state.model || '',
      'Arrow Shaft':
        state.productTitle || (state.model ? state.model + ' ' : '') + state.spine + ' Spine ' + packLabel,
      Spine: state.spine,
      'Insert / Halfout':
        state.insert && state.insert !== 'None'
          ? withMarkup(state.insert, state.insertPrice)
          : state.insert || 'None',
      'Field points':
        state.fieldPoints && state.fieldPoints !== 'None'
          ? withMarkup(state.fieldPointsLabel || state.fieldPoints, state.fieldPointsPrice)
          : state.fieldPointsLabel || state.fieldPoints || 'None',
      Fletching: state.fletchVariantId
        ? withMarkup(
            (state.fletch || '3 fletch') +
              (state.fletchLabel ? ' — ' + state.fletchLabel : ''),
            state.fletchPrice
          )
        : state.wrapLaborVariantId
          ? withMarkup(
              (state.fletch || '3 fletch') + ' — DIY fletch; TitleX wrap install',
              state.wrapLaborPrice
            )
          : state.laborMode === 'diy'
            ? (state.fletch || '3 fletch') + ' — DIY labor (customer installs)'
            : state.laborMode === 'none'
              ? 'None (bare / components only)'
              : state.fletch || '3 fletch',
      Helical: state.helical === 'left' ? 'Left' : state.helical === 'none' ? 'Straight' : 'Right',
      Vane: state.laborMode === 'none' ? '' : vaneProductByStyle(state.vaneStyle).name,
      'Vane Primary Color': state.laborMode === 'none' ? '' : state.vanePrimary || '',
      'Vane Secondary Color': state.laborMode === 'none' ? '' : state.vaneSecondary || '',
      Nocks: isTargetModel(state.modelKey) ? 'Black nock bushing (included)' : 'Standard (included)',
      'Nock Color': isTargetModel(state.modelKey) ? 'Black' : state.nockColor || '',
      'Lighted Nocks':
        isTargetModel(state.modelKey)
          ? 'No'
          : state.lightedNocks === 'Yes'
            ? withMarkup('Yes — upcharge (standard nocks still included)', state.lightedNocksPrice)
            : 'No — standard nocks only',
      'Pin Nock & Bushing': state.pinNock || 'No',
      _build_id: buildId,
      _build_labor: state.laborMode || 'titlex',
      Wraps: state.wraps || 'None',
    };
    if (state.wrapColor) {
      properties['Wrap Color'] = state.wrapColor;
    }
    if (state.totalWeightGr) {
      properties['Total Weight'] = state.totalWeightGr + ' gr';
    }
    if (state.focPercent != null && !Number.isNaN(state.focPercent)) {
      properties['Est. FOC'] = state.focPercent.toFixed(1) + '%';
    }
    if (state.cutLength) properties['Cut Length'] = state.cutLength;
    if (state.lightedNocks === 'Yes') {
      properties['Lighted Nock Color'] = state.lightedNockColor || 'Bright Green';
    }

    const items = [];

    /**
     * Cover arrowCount with dozen + half-dozen SKUs.
     * Never treat a dozen SKU as a half-dozen (that was doubling shafts).
     * mode:
     *  - 'packs': use num12pk / num6pk slot counts (wraps without pack labels)
     *  - 'arrows': cover exact arrow count using unit sizes (shafts, cuts, lighted, FP)
     */
    function pushCovering(opts) {
      const id12 = opts.id12;
      const id6 = opts.id6;
      const unit12 = opts.unit12 || 12;
      const unit6 = opts.unit6 || 6;
      const props = opts.props || {};
      const mode = opts.mode || 'arrows';
      const arrowCount = state.arrowCount;
      const num12 = state.num12pk;
      const num6 = state.num6pk;

      if (mode === 'packs') {
        // One line unit per pack slot (classic wrap behavior when SKU has no pack option)
        const sku = id12 || id6;
        if (!sku) return;
        const qty = (id12 && id6 && String(id12) !== String(id6) ? 0 : num12 + num6) || 0;
        if (id12 && id6 && String(id12) !== String(id6)) {
          if (num12 > 0) items.push({ id: parseInt(id12, 10), quantity: num12, properties: props });
          if (num6 > 0) items.push({ id: parseInt(id6, 10), quantity: num6, properties: props });
        } else if (qty > 0) {
          items.push({ id: parseInt(sku, 10), quantity: qty, properties: props });
        }
        return;
      }

      let remaining = arrowCount;
      if (id12 && unit12 === 12 && remaining >= 12) {
        const n = Math.floor(remaining / 12);
        items.push({ id: parseInt(id12, 10), quantity: n, properties: props });
        remaining -= n * 12;
      }
      if (remaining > 0 && id6) {
        const n = Math.ceil(remaining / unit6);
        items.push({ id: parseInt(id6, 10), quantity: n, properties: props });
        remaining -= n * unit6;
      } else if (remaining > 0 && id12 && unit12 === 6) {
        items.push({ id: parseInt(id12, 10), quantity: Math.ceil(remaining / 6), properties: props });
      } else if (remaining > 0 && id12 && !id6) {
        // No half-dozen SKU — only exact dozens can be fulfilled here
        throw new Error('Missing half-dozen pack to cover ' + arrowCount + ' arrows.');
      }
    }

    if (state.baseVariantId || state.baseVariantId6pk) {
      pushCovering({
        id12: state.baseVariantId,
        id6: state.baseVariantId6pk,
        unit12: state.baseUnit12 || 12,
        unit6: state.baseUnit6 || 6,
        props: properties,
        mode: 'arrows',
      });
    }

    if (state.fletchVariantId) {
      items.push({
        id: parseInt(state.fletchVariantId, 10),
        quantity: Math.max(1, state.fletchDozenQty || 1),
        properties: {
          _build_id: buildId,
          _build_labor: state.laborMode || 'titlex',
          Fletching: state.fletch || '3 fletch',
          Helical:
            state.helical === 'left' ? 'Left' : state.helical === 'none' ? 'Straight' : 'Right',
          Vane: vaneProductByStyle(state.vaneStyle).name,
          'Vane Primary Color': state.vanePrimary || '',
          'Vane Secondary Color': state.vaneSecondary || '',
        },
      });
    }

    if (state.wrapLaborVariantId) {
      items.push({
        id: parseInt(state.wrapLaborVariantId, 10),
        quantity: Math.max(1, state.wrapLaborDozenQty || 1),
        properties: {
          _build_id: buildId,
          _build_labor: state.laborMode || 'wrap_only',
          Fletching: 'DIY (customer)',
          'Wrap Labor': state.wrapLaborLabel || 'Wrap install only',
          Wraps: state.wraps || '',
        },
      });
    }

    const lineProps = { _build_id: buildId, _build_labor: state.laborMode || 'titlex' };
    if (state.insertVariantId || state.insertVariantId6pk) {
      pushCovering({
        id12: state.insertVariantId,
        id6: state.insertVariantId6pk,
        props: lineProps,
        mode: 'packs',
      });
    }
    if (state.fieldPointsVariantId || state.fieldPointsVariantId6pk) {
      pushCovering({
        id12: state.fieldPointsVariantId,
        id6: state.fieldPointsVariantId6pk,
        unit12: 12,
        unit6: 6,
        props: lineProps,
        mode: 'arrows',
      });
    }
    if (state.wrapsVariantId || state.wrapsVariantId6pk) {
      function wrapLineProps(collectiveId) {
        const p = {
          _build_id: buildId,
          _build_labor: state.laborMode || 'titlex',
        };
        if (collectiveId) p._collective_variant_id = String(collectiveId);
        if (state.wraps && state.wraps !== 'None') p.Wraps = state.wraps;
        if (state.wrapColor) p['Wrap Color'] = state.wrapColor;
        if (state.wrapKey) p._wrap_handle = String(state.wrapKey);
        return p;
      }
      const cart12 = state.wrapsVariantId;
      const cart6 = state.wrapsVariantId6pk;
      const col12 = state.wrapsCollectiveId12 || '';
      const col6 = state.wrapsCollectiveId6 || col12;
      const num12 = state.num12pk;
      const num6 = state.num6pk;
      if (cart12 && cart6 && String(cart12) !== String(cart6)) {
        if (num12 > 0) items.push({ id: parseInt(cart12, 10), quantity: num12, properties: wrapLineProps(col12) });
        if (num6 > 0) items.push({ id: parseInt(cart6, 10), quantity: num6, properties: wrapLineProps(col6) });
      } else {
        const sku = cart12 || cart6;
        if (num12 > 0 && num6 > 0 && col12 && col6 && String(col12) !== String(col6)) {
          items.push({ id: parseInt(sku, 10), quantity: num12, properties: wrapLineProps(col12) });
          items.push({ id: parseInt(sku, 10), quantity: num6, properties: wrapLineProps(col6) });
        } else if (num12 + num6 > 0 && sku) {
          items.push({
            id: parseInt(sku, 10),
            quantity: num12 + num6,
            properties: wrapLineProps(col12 || col6),
          });
        }
      }
    }
    if (state.lightedNocksVariantId6pk || state.lightedNocksVariantId) {
      pushCovering({
        id12: state.lightedNocksVariantId,
        id6: state.lightedNocksVariantId6pk,
        unit12: 12,
        unit6: state.lightedNocksUnit6 || 6,
        props: lineProps,
        mode: 'arrows',
      });
    }
    if (state.cutProductVariantId || state.cutProductVariantId6pk) {
      pushCovering({
        id12: state.cutProductVariantId,
        id6: state.cutProductVariantId6pk,
        unit12: 12,
        unit6: 6,
        props: {
          'Cut Length': state.cutLength || '',
          _build_id: buildId,
          _build_labor: state.laborMode || 'titlex',
        },
        mode: 'arrows',
      });
    }

    return mergeDuplicateItems(items);
  }

  function getLineItemBuildId(item) {
    const p = item.properties;
    if (!p) return null;
    if (typeof p === 'object' && !Array.isArray(p)) return p._build_id || null;
    if (Array.isArray(p)) {
      for (let i = 0; i < p.length; i++) {
        const row = p[i];
        if (row && (row.name === '_build_id' || row.key === '_build_id')) return row.value;
      }
    }
    return null;
  }

  function removeBuildLinesFromCart(buildId) {
    if (!buildId) return Promise.resolve(null);
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        const updates = {};
        (cart.items || []).forEach(function (item) {
          if (getLineItemBuildId(item) === buildId) {
            if (item.key) updates[item.key] = 0;
            else if (item.id != null) updates[String(item.id)] = 0;
          }
        });
        if (Object.keys(updates).length === 0) return cart;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates }),
        }).then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) throw new Error((body && (body.description || body.message)) || 'Cart update failed');
            return body;
          });
        });
      });
  }

  function refreshCartDrawer(openDrawer) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof window.routes === 'undefined') return Promise.resolve();
    return fetch(window.routes.cart_url + '?section_id=cart-drawer')
      .then(function (res) {
        return res.text();
      })
      .then(function (htmlText) {
        if (typeof window.applyCartDrawerSectionHtml === 'function') {
          window.applyCartDrawerSectionHtml(htmlText);
        } else {
          const html = new DOMParser().parseFromString(htmlText, 'text/html');
          ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (selector) {
            const target = document.querySelector(selector);
            const source = html.querySelector(selector);
            if (target && source) target.replaceWith(source);
          });
          if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
        }
        if (openDrawer !== false && typeof cartDrawer.open === 'function') cartDrawer.open();
      })
      .catch(function (e) {
        console.error('[Arrow Playground] cart refresh failed', e);
      });
  }

  function refreshCartIconBubble() {
    if (typeof window.routes === 'undefined') return Promise.resolve();
    return fetch(window.routes.cart_url + '?section_id=cart-icon-bubble')
      .then(function (res) {
        return res.text();
      })
      .then(function (htmlText) {
        const html = new DOMParser().parseFromString(htmlText, 'text/html');
        const sourceSection =
          html.querySelector('#shopify-section-cart-icon-bubble') || html.querySelector('.shopify-section');
        const target = document.querySelector('#cart-icon-bubble');
        if (sourceSection && target) target.innerHTML = sourceSection.innerHTML;
      })
      .catch(function (e) {
        console.error('[Arrow Playground] cart icon refresh failed', e);
      });
  }

  function publishCartUpdate(payload) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      return publish(PUB_SUB_EVENTS.cartUpdate, payload);
    }
    return Promise.resolve();
  }

  function afterCartMutation(cartData, openDrawer) {
    return Promise.all([
      refreshCartDrawer(openDrawer),
      refreshCartIconBubble(),
      publishCartUpdate({ source: 'arrow-playground', cartData: cartData }),
    ]);
  }

  function waitAnimationFrames(count) {
    return new Promise(function (resolve) {
      function step() {
        count -= 1;
        if (count <= 0) resolve();
        else requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    });
  }

  function capturePreviewFile(viewer, host, buildId) {
    const mv =
      (viewer && typeof viewer.getViewer === 'function' && viewer.getViewer()) ||
      (host && host.querySelector('model-viewer'));
    if (!mv || typeof mv.toBlob !== 'function') return Promise.resolve(null);
    let resume = false;
    try {
      resume = !!(viewer && typeof viewer.isSpinning === 'function' && viewer.isSpinning());
      if (resume && typeof viewer.setSpinning === 'function') viewer.setSpinning(false);
    } catch (e) {
      resume = false;
    }
    return waitAnimationFrames(2)
      .then(function () {
        return mv.toBlob({ mimeType: 'image/jpeg', qualityArgument: 0.84, idealAspect: false });
      })
      .then(function (blob) {
        if (resume && viewer && typeof viewer.setSpinning === 'function') viewer.setSpinning(true);
        if (!blob || !blob.size) return null;
        const name = 'custom-arrow-' + String(buildId || 'preview').replace(/[^\w-]+/g, '') + '.jpg';
        try {
          return new File([blob], name, { type: 'image/jpeg' });
        } catch (e) {
          return blob;
        }
      })
      .catch(function (err) {
        try {
          if (resume && viewer && typeof viewer.setSpinning === 'function') viewer.setSpinning(true);
        } catch (e2) {
          /* ignore */
        }
        console.warn('[Arrow Playground] preview capture failed', err);
        return null;
      });
  }

  function itemsToFormData(items, previewFile) {
    const form = new FormData();
    items.forEach(function (item, i) {
      form.append('items[' + i + '][id]', String(item.id));
      form.append('items[' + i + '][quantity]', String(item.quantity));
      const props = item.properties || {};
      Object.keys(props).forEach(function (key) {
        if (props[key] == null || props[key] === '') return;
        form.append('items[' + i + '][properties][' + key + ']', String(props[key]));
      });
    });
    if (previewFile && items.length) {
      form.append(
        'items[0][properties][Arrow Preview]',
        previewFile,
        previewFile.name || 'custom-arrow-preview.jpg'
      );
    }
    return form;
  }

  function postCartItems(items, previewFile) {
    if (previewFile) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: itemsToFormData(items, previewFile),
      });
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    });
  }

  function syncDiyCartAttributes(laborMode, hasWrap) {
    const attributes = {
      _diy_fletch: '',
      _diy_wrap_install: '',
    };
    if (laborMode === 'diy') {
      attributes._diy_fletch = '1';
      attributes._diy_wrap_install = hasWrap ? '1' : '';
    } else if (laborMode === 'wrap_only') {
      attributes._diy_fletch = '1';
      attributes._diy_wrap_install = '';
    } else if (laborMode === 'none') {
      // Bare: no fletch consent. Wrap without wrap_only → customer installs wraps.
      attributes._diy_wrap_install = hasWrap ? '1' : '';
    }
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) {
          console.warn('[Arrow Playground] DIY attribute sync failed', body);
        }
        return body;
      });
    });
  }

  function addBuildToCart(viewerState, qty, catalog, buildId, viewer, host) {
    const state = resolveCartState(viewerState, qty, catalog);
    const items = buildItems(state, buildId);
    if (!items.length) return Promise.reject(new Error('Nothing to add — check product settings.'));
    return removeBuildLinesFromCart(buildId)
      .then(function () {
        return capturePreviewFile(viewer, host, buildId);
      })
      .then(function (previewFile) {
        return postCartItems(items, previewFile).then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok && previewFile) {
              console.warn('[Arrow Playground] cart add with preview failed, retrying without file', data);
              return postCartItems(items, null).then(function (res2) {
                return res2.json().then(function (data2) {
                  return { res: res2, data: data2, state: state };
                });
              });
            }
            return { res: res, data: data, state: state };
          });
        });
      })
      .then(function (result) {
        if (!result.res.ok) {
          const message =
            (result.data && (result.data.description || result.data.message)) || 'Could not add to cart.';
          return afterCartMutation(result.data, true).then(function () {
            throw new Error(message);
          });
        }
        const hasWrap = !!(state.wraps && state.wraps !== 'None');
        return syncDiyCartAttributes(state.laborMode || 'titlex', hasWrap)
          .then(function () {
            return afterCartMutation(result.data, true);
          })
          .then(function () {
            return result;
          });
      });
  }

  function matchNamedColor(raw, names) {
    const n = String(raw || '')
      .trim()
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ');
    if (!n || !names || !names.length) return '';
    if ((n === 'fire orange' || n.indexOf('fire orange') >= 0) && names.indexOf('Orange') >= 0) {
      return 'Orange';
    }
    for (let i = 0; i < names.length; i++) {
      if (names[i].toLowerCase() === n) return names[i];
    }
    for (let i = 0; i < names.length; i++) {
      const c = names[i].toLowerCase();
      if (n.indexOf(c) >= 0 || c.indexOf(n) >= 0) return names[i];
    }
    return '';
  }

  const DEEP_LINK_PARTS = ['shaft', 'wrap', 'insert', 'nock', 'point', 'vane'];

  // Keep in sync with scripts/playground/part-camera-focus.mjs
  // GLB: nock ~ -7.2, wrap ~ -5.2, vanes ~ -6.4, insert/point ~ +7.1 / +7.5
  const PART_CAMERAS = {
    shaft: { orbit: '-52deg 70deg 55%', fov: '22deg', target: 'auto' },
    wrap: { orbit: '-52deg 72deg 7.2m', fov: '22deg', target: '-5.2m 0.06m 0m' },
    vane: { orbit: '-48deg 72deg 5.8m', fov: '20deg', target: '-6.4m 0.14m 0m' },
    nock: { orbit: '-45deg 72deg 5.4m', fov: '20deg', target: '-7.2m 0.06m 0m' },
    insert: { orbit: '-52deg 72deg 5.6m', fov: '20deg', target: '7.05m 0.04m 0m' },
    point: { orbit: '-52deg 72deg 5.4m', fov: '20deg', target: '7.55m 0.04m 0m' },
  };

  /** Keep in sync with scripts/playground/part-camera-focus.mjs */
  const CONTROL_GROUP_TO_PART = {
    wrap: 'wrap',
    nockColor: 'nock',
    lighted: 'nock',
    lightedNockColor: 'nock',
    vanePrimary: 'vane',
    vaneSecondary: 'vane',
    vaneProduct: 'vane',
    helical: 'vane',
    fletch: 'vane',
    insert: 'insert',
    insertAluminumOd: 'insert',
    fieldPoint: 'point',
    fieldPointGrain: 'point',
  };

  function controlGroupToPart(group) {
    if (!group) return null;
    return CONTROL_GROUP_TO_PART[group] || null;
  }

  function shouldFocusPartCamera(group) {
    return !!controlGroupToPart(group);
  }

  function groupFromControlTarget(target) {
    if (!target || typeof target.closest !== 'function') return null;
    const section = target.closest('[data-group]');
    if (!section) return null;
    return section.getAttribute('data-group') || (section.dataset && section.dataset.group) || null;
  }

  function parseBuilderDeepLink(search) {
    let params;
    try {
      params = new URLSearchParams(search || '');
    } catch (e) {
      return null;
    }
    const part = String(params.get('ab') || '')
      .trim()
      .toLowerCase();
    if (DEEP_LINK_PARTS.indexOf(part) < 0) return null;

    const patch = {};
    const model = String(params.get('model') || '').toLowerCase();
    if (model === 'tx5' || model === 'tx23' || model === 'machxv') patch.model = model;

    const spine = String(params.get('spine') || '').replace(/[^0-9]/g, '');
    if (['250', '300', '350', '400'].indexOf(spine) >= 0) patch.spine = spine;

    const wrap = String(params.get('wrap') || '').trim();
    if (wrap) patch.wrapKey = wrap === 'none' ? 'none' : wrap;

    const insert = String(params.get('insert') || '').toLowerCase();
    if (insert === 'off' || insert === 'none') {
      patch.insert = false;
    } else if (insert === 'brass' || insert === 'stainless' || insert === 'aluminum') {
      patch.insert = true;
      patch.insertFinish = insert;
    }
    const od = String(params.get('od') || params.get('insertOd') || '');
    if (od.indexOf('7.1') >= 0) patch.insertAluminumOd = '7.1mm';
    else if (od.indexOf('6.8') >= 0) patch.insertAluminumOd = '6.8mm';

    const lightedRaw = String(params.get('lighted') || '').toLowerCase();
    if (lightedRaw === '1' || lightedRaw === 'yes' || lightedRaw === 'true') {
      patch.lightedNocks = true;
    }
    const nockRaw = params.get('nock');
    if (nockRaw) {
      if (patch.lightedNocks) {
        patch.lightedNockColor = matchNamedColor(nockRaw, LIGHTED_NOCK_COLOR_NAMES) || 'Bright Green';
      } else {
        const std = matchNamedColor(nockRaw, NOCK_COLOR_NAMES);
        if (std) patch.nockColor = std;
      }
    }

    const point = String(params.get('point') || '').toLowerCase();
    if (point === 'none' || point === 'off') {
      patch.fieldPoint = false;
      patch.fieldPointFinish = 'none';
    } else if (point === 'black') {
      patch.fieldPoint = true;
      patch.fieldPointFinish = 'black';
    } else if (point === 'stainless') {
      patch.fieldPoint = true;
      patch.fieldPointFinish = 'stainless';
    }
    const grain = parseInt(params.get('grain'), 10);
    if (grain === 120 || grain === 200) {
      patch.fieldPointGrain = grain;
      patch.fieldPointFinish = 'gluein';
      if (!patch.model) patch.model = 'tx23';
    } else if (grain) {
      patch.fieldPointGrain = fieldPointGrainForFinish(
        grain,
        patch.fieldPointFinish || 'stainless'
      );
    }

    const vane = matchNamedColor(params.get('vane'), VANE_COLOR_NAMES);
    if (vane) patch.vanePrimary = vane;
    const vane2 = matchNamedColor(params.get('vane2'), VANE_COLOR_NAMES);
    if (vane2) patch.vaneSecondary = vane2;

    return { part: part, patch: patch };
  }

  /**
   * Classic wizard handoff removed — ATC writes the cart directly.
   */

  ready(function () {
    const root = document.querySelector('[data-arrow-playground]');
    if (!root) return;

    const host = root.querySelector('[data-arrow-playground-host]');
    const controls = root.querySelector('[data-arrow-playground-controls]');
    const qtyInput = root.querySelector('[data-arrow-playground-qty]');
    const atcBtn = root.querySelector('[data-arrow-playground-atc]');
    const priceEl = root.querySelector('[data-arrow-playground-price]');
    const errorEl = root.querySelector('[data-arrow-playground-error]');
    const catalog = parseCatalog(root);
    const sessionBuildId = newBuildId();

    if (!host || !controls) return;

    disableStickyHeader();
    syncViewportFill(root);
    window.addEventListener('resize', function () {
      syncViewportFill(root);
    });
    // Re-measure after theme chrome / announcement hide settles
    [50, 200, 500, 1000].forEach(function (ms) {
      window.setTimeout(function () {
        disableStickyHeader();
        syncViewportFill(root);
      }, ms);
    });

    console.log('[Arrow Playground] boot', { href: location.href, build: 'sticky1' });

    function getQty() {
      return qtyInput ? parseInt(qtyInput.value, 10) || 12 : 12;
    }

    function setQty(qty) {
      if (!qtyInput || qty == null || Number.isNaN(qty)) return;
      qtyInput.value = String(Math.min(36, Math.max(6, qty)));
    }

    function setError(message) {
      if (!errorEl) return;
      if (!message) {
        errorEl.hidden = true;
        errorEl.textContent = '';
        return;
      }
      errorEl.hidden = false;
      errorEl.textContent = message;
    }

    const deepLink = parseBuilderDeepLink(location.search);
    const saved = deepLink ? null : loadPersisted();
    let state = Object.assign({}, DEMO_STATE, saved && saved.state ? saved.state : {});
    if (deepLink && deepLink.patch) {
      state = Object.assign(state, deepLink.patch);
    }
    state.lightedNocks =
      state.lightedNocks === true ||
      state.lightedNocks === 'yes' ||
      state.lightedNocks === 'Yes';
    if (state.vaneStyle !== 'alpha') state.vaneStyle = 'hybrid';
    const bootVaneColors = vaneColorNames(state.vaneStyle);
    if (bootVaneColors.indexOf(state.vanePrimary) < 0) {
      state.vanePrimary = state.vaneStyle === 'alpha' ? 'Yellow' : 'Bright Green';
    }
    if (bootVaneColors.indexOf(state.vaneSecondary) < 0) {
      state.vaneSecondary = state.vaneStyle === 'alpha' ? 'White' : 'OD Green';
    }
    if (isTargetModel(state.model)) {
      Object.assign(state, targetHardwarePatch(normalizeGlueInGrain(state.fieldPointGrain)));
    } else {
      clearGlueInFromHunting(state);
      state.fieldPointGrain = fieldPointGrainForFinish(
        state.fieldPointGrain,
        state.fieldPointFinish || 'stainless'
      );
    }
    // Labor picker was removed from the builder; ATC always TitleX-fletches.
    state.laborMode = 'titlex';
    if (saved && saved.qty != null) setQty(saved.qty);

    function snapshotHunting(s) {
      return {
        insert: s.insert,
        insertFinish: s.insertFinish,
        insertAluminumOd: s.insertAluminumOd,
        fieldPoint: s.fieldPoint,
        fieldPointFinish: s.fieldPointFinish,
        fieldPointGrain: s.fieldPointGrain,
        nockColor: s.nockColor,
        lightedNocks: s.lightedNocks,
        lightedNockColor: s.lightedNockColor,
      };
    }

    let lastHuntingHardware = isTargetModel(state.model) ? null : snapshotHunting(state);

    function modelSwitchPatch(prev, nextModel) {
      const patch = { model: nextModel };
      const wasTarget = isTargetModel(prev.model);
      const nowTarget = isTargetModel(nextModel);
      if (wasTarget === nowTarget) return patch;
      if (nowTarget) {
        lastHuntingHardware = snapshotHunting(prev);
        return Object.assign(patch, targetHardwarePatch(120));
      }
      const restored = lastHuntingHardware
        ? Object.assign({}, lastHuntingHardware)
        : {
            insert: true,
            insertFinish: 'brass',
            insertAluminumOd: prev.insertAluminumOd || '6.8mm',
            fieldPoint: true,
            fieldPointFinish: 'stainless',
            fieldPointGrain: 100,
            nockColor: 'Orange',
            lightedNocks: false,
            lightedNockColor: prev.lightedNockColor,
          };
      clearGlueInFromHunting(restored);
      return Object.assign(patch, restored);
    }

    const weightEl = root.querySelector('[data-arrow-playground-weight]');
    const focEl = root.querySelector('[data-arrow-playground-foc]');
    const weightNoteEl = root.querySelector('[data-arrow-playground-weight-note]');

    function refreshStats() {
      const stats = estimateBuildBallistics(state, catalog);
      logBuildBallistics(stats, state);
      if (weightEl) weightEl.textContent = stats.totalGr + ' GR';
      if (focEl) focEl.textContent = stats.focPercent.toFixed(1) + '%';
      if (weightNoteEl) {
        const pointKind = isTargetModel(state.model) ? 'glue-in point' : 'field point';
        const vaneCount = state.fletchCount >= 4 ? 4 : 3;
        weightNoteEl.textContent =
          (stats.includesPoint ? 'Including ' : 'Without ') +
          pointKind +
          ' · ' +
          vaneCount +
          ' × ' +
          vaneGrainsEach(state.vaneStyle) +
          ' gr ' +
          vaneProductByStyle(state.vaneStyle).name;
      }
      const statsRoot = root.querySelector('[data-arrow-playground-stats]');
      if (statsRoot) {
        statsRoot.title =
          stats.gpi.toFixed(1) +
          ' GPI × ' +
          stats.lengthIn +
          '" shaft · ' +
          (state.fletchCount >= 4 ? 4 : 3) +
          ' × ' +
          vaneGrainsEach(state.vaneStyle) +
          ' gr vanes · estimated, not a scale reading';
      }
    }

    function refreshPrice() {
      refreshStats();
      if (!priceEl) return;
      if (!catalog || !catalog.shafts) {
        priceEl.textContent = 'Set products in theme editor';
        return;
      }
      try {
        const cartState = resolveCartState(state, getQty(), catalog);
        priceEl.textContent = formatMoney(computeTotal(cartState), catalog.moneyFormat);
        setError('');
      } catch (e) {
        const msg = e && e.message ? String(e.message) : 'Out of stock';
        priceEl.textContent = 'Out of stock';
        priceEl.title = msg;
        setError(msg);
      }
    }

    const optionButtons = new Map();

    function rememberOption(groupKey, key, el, baseLabel) {
      if (!optionButtons.has(groupKey)) optionButtons.set(groupKey, []);
      optionButtons.get(groupKey).push({
        key: key,
        el: el,
        baseLabel: baseLabel != null ? String(baseLabel) : String(key),
      });
    }

    function applySoldOutUi(el, soldOut, name) {
      if (!el) return;
      const label = String(name || '').trim() || 'This option';
      el.disabled = !!soldOut;
      el.classList.toggle('is-sold-out', !!soldOut);
      if (soldOut) {
        el.setAttribute('aria-disabled', 'true');
        el.title = label + ' is out of stock';
        el.setAttribute('aria-label', label + ', out of stock');
      } else {
        el.removeAttribute('aria-disabled');
        if (name) el.title = label;
      }
      const cap =
        el.parentNode &&
        el.parentNode.querySelector &&
        el.parentNode.querySelector('.arrow-playground__swatch-caption');
      if (cap) cap.textContent = soldOut ? soldOutLabel(label) : label;
      if (el.classList.contains('arrow-playground__option')) {
        el.textContent = soldOut ? soldOutLabel(label) : label;
      }
      const stockEl = el.querySelector && el.querySelector('.arrow-playground__wrap-stock');
      if (stockEl) {
        stockEl.hidden = !soldOut;
        stockEl.textContent = soldOut ? 'Out of stock' : '';
      }
      const wrapPriceEl = el.querySelector && el.querySelector('.arrow-playground__wrap-price');
      if (wrapPriceEl) wrapPriceEl.hidden = !!soldOut;
    }

    function catalogVariants() {
      const list = [];
      for (let i = 0; i < arguments.length; i++) {
        const product = arguments[i];
        if (product && Array.isArray(product.variants)) {
          Array.prototype.push.apply(list, product.variants);
        }
      }
      return list;
    }

    function shaftVariantsFor(modelKey) {
      const key = modelKey || state.model || 'tx5';
      return catalogVariants(
        catalog.shafts && catalog.shafts[key],
        catalog.shafts6 && catalog.shafts6[key]
      );
    }

    function wrapSizePool(product) {
      const variants = product && Array.isArray(product.variants) ? product.variants : [];
      if (!variants.length) return [];
      const sizeToken = wrapSizeTokenForModel(state.model || 'tx5');
      const sized = variants.filter(function (v) {
        return variantHasSize(v, sizeToken);
      });
      if (sized.length) return sized;
      const hasSizeAxis = variants.some(function (v) {
        return (
          variantHasSize(v, '5mm') ||
          variantHasSize(v, '23dia') ||
          variantHasSize(v, '6.5mm')
        );
      });
      return hasSizeAxis ? [] : variants;
    }

    function wrapHandleSoldOut(handle) {
      const product = catalog && catalog.wraps && catalog.wraps[handle];
      if (!product || !product.variants || !product.variants.length) return false;
      const pool = wrapSizePool(product);
      if (!pool.length) return true;
      return isSoldOut(pool, null, true);
    }

    function wrapColorSoldOut(handle, colorName) {
      const product = catalog && catalog.wraps && catalog.wraps[handle];
      if (!product || !product.variants || !product.variants.length) return false;
      const pool = wrapSizePool(product);
      if (!pool.length) return true;
      if (!colorName) return isSoldOut(pool, null, true);
      return isSoldOut(
        pool,
        function (v) {
          return variantHasColor(v, colorName);
        },
        false
      );
    }

    function optionIsSoldOut(groupKey, key) {
      if (groupKey === 'model') {
        return isSoldOut(shaftVariantsFor(key), null, true);
      }
      if (groupKey === 'spine') {
        return isSoldOut(
          shaftVariantsFor(state.model),
          function (v) {
            return hasSpine(v, key);
          },
          true
        );
      }
      if (groupKey === 'insert') {
        if (key === 'off') return false;
        const product = catalog.inserts && catalog.inserts[key];
        return isSoldOut(catalogVariants(product), null, true);
      }
      if (groupKey === 'insertAluminumOd') {
        return isSoldOut(
          catalogVariants(catalog.inserts && catalog.inserts.aluminum),
          function (v) {
            const blob = normOpt(variantOptions(v).concat([v.title]).join(' '));
            const want = normOpt(key);
            return blob.indexOf(want) >= 0 || blob.indexOf(want.replace('mm', '')) >= 0;
          },
          true
        );
      }
      if (groupKey === 'fieldPoint') {
        if (key === 'none') return false;
        return isSoldOut(
          catalogVariants(catalog.fieldPoints),
          function (v) {
            return fieldPointFinishMatch(v, key);
          },
          true
        );
      }
      if (groupKey === 'fieldPointGrain') {
        if (isTargetModel(state.model)) {
          return isSoldOut(
            shaftVariantsFor(state.model),
            function (v) {
              return hasSpine(v, state.spine) && hasGlueInPoint(v, key);
            },
            true
          );
        }
        const finish = state.fieldPointFinish || 'stainless';
        if (finish === 'none' || finish === 'gluein') return false;
        return isSoldOut(
          catalogVariants(catalog.fieldPoints),
          function (v) {
            return fieldPointFinishMatch(v, finish) && fieldPointGrainMatch(v, key);
          },
          true
        );
      }
      if (groupKey === 'lighted') {
        if (key !== 'yes') return false;
        return isSoldOut(catalogVariants(catalog.lightedNocks), null, true);
      }
      if (groupKey === 'lightedNockColor') {
        return isSoldOut(
          catalogVariants(catalog.lightedNocks),
          function (v) {
            const blob = normOpt(variantOptions(v).concat([v.title]).join(' '));
            const want = normOpt(key);
            return blob === want || (want && blob.indexOf(want) >= 0);
          },
          false
        );
      }
      if (groupKey === 'wrap') {
        if (key === 'none') return false;
        return wrapHandleSoldOut(key);
      }
      return false;
    }

    function syncSoldOutOptions() {
      optionButtons.forEach(function (items, groupKey) {
        items.forEach(function (item) {
          const soldOut = optionIsSoldOut(groupKey, item.key);
          applySoldOutUi(item.el, soldOut, item.baseLabel);
        });
      });
    }

    function setActive(groupKey, current, labelText) {
      const items = optionButtons.get(groupKey) || [];
      items.forEach(function (item) {
        let active = item.key === current;
        if (groupKey === 'wrap') {
          if (item.key === 'none') {
            active = current === 'none' || !current;
          } else {
            active = wrapBaseHandle(current) === item.key;
          }
        }
        item.el.classList.toggle('is-active', active);
      });
      const valueEl = controls.querySelector(
        '[data-group-value="' + groupKey + '"]'
      );
      if (valueEl) {
        valueEl.textContent =
          labelText != null && labelText !== ''
            ? String(labelText)
            : current == null
              ? ''
              : String(current);
      }
    }

    function sectionHead(title, groupKey, currentLabel, note) {
      const section = document.createElement('div');
      section.className = 'arrow-playground__section';
      if (groupKey) section.dataset.group = groupKey;
      const head = document.createElement('div');
      head.className = 'arrow-playground__section-head';
      const h = document.createElement('h2');
      h.textContent = title;
      const val = document.createElement('span');
      val.className = 'arrow-playground__section-value';
      val.dataset.groupValue = groupKey;
      val.textContent = currentLabel == null ? '' : String(currentLabel);
      head.appendChild(h);
      head.appendChild(val);
      section.appendChild(head);
      if (note) {
        const n = document.createElement('p');
        n.className = 'arrow-playground__section-note';
        n.textContent = note;
        section.appendChild(n);
      }
      return section;
    }

    function specCallout(title, groupKey, label, note) {
      const section = sectionHead(title, groupKey, label, note);
      const card = document.createElement('div');
      card.className = 'arrow-playground__spec-card';
      card.textContent = label;
      section.appendChild(card);
      controls.appendChild(section);
      return section;
    }

    function syncSpineLabels() {
      const labels = spineLabelsFor(state.model);
      const items = optionButtons.get('spine') || [];
      items.forEach(function (item) {
        const label = labels[item.key] || String(item.key);
        item.baseLabel = label;
        item.el.textContent = label;
        item.el.title = label;
      });
      const current = String(state.spine || '250');
      setActive('spine', current, labels[current] || current);
    }

    function bootViewer() {
      const api = window.ArrowViewer;
      if (!api || typeof api.createArrowViewer !== 'function') {
        setTimeout(bootViewer, 50);
        return;
      }

      const viewer = api.createArrowViewer(host, {
        modelSrc: root.dataset.modelSrc,
        wrapsManifestUrl: root.dataset.wrapsManifest,
        wrapTextureBaseUrl: root.dataset.wrapTextureBase || '',
        vanesManifestUrl: root.dataset.vanesManifest,
        placeholderSampleUrl: root.dataset.placeholderSample || '',
        placeholderBaseUrl: '',
        preferVaneTextures: true,
        initialState: state,
        // Closer framing so the arrow fills the stage (lower orbit % = nearer)
        // Nock near camera, bottom-left → tip away top-right.
        cameraOrbit: '-52deg 70deg 55%',
        fieldOfView: '22deg',
      });

      function enableFreeCamera() {
        const mv = viewer.getViewer();
        if (!mv) return;
        try {
          mv.setAttribute('camera-controls', '');
          mv.removeAttribute('disable-pan');
          mv.removeAttribute('disable-zoom');
          mv.setAttribute('touch-action', 'none');
          mv.setAttribute('min-camera-orbit', 'auto auto 0.05m');
          mv.setAttribute('max-camera-orbit', 'auto auto 400%');
        } catch (e) {
          /* ignore */
        }
      }
      enableFreeCamera();
      if (viewer.ready && typeof viewer.ready.then === 'function') {
        viewer.ready.then(enableFreeCamera);
      }

      async function apply(patch, active) {
        state = Object.assign({}, state, patch);
        if (isTargetModel(state.model)) {
          Object.assign(state, targetHardwarePatch(normalizeGlueInGrain(state.fieldPointGrain)));
        } else if (
          Object.prototype.hasOwnProperty.call(patch, 'fieldPointFinish') ||
          Object.prototype.hasOwnProperty.call(patch, 'fieldPointGrain')
        ) {
          const nextGrain = fieldPointGrainForFinish(
            state.fieldPointGrain,
            state.fieldPointFinish || 'stainless'
          );
          if (nextGrain !== normalizeFieldPointGrain(state.fieldPointGrain)) {
            state.fieldPointGrain = nextGrain;
            if (!(active && active.group === 'fieldPointGrain')) {
              setActive('fieldPointGrain', String(nextGrain), nextGrain + ' gr');
            }
          }
        }
        if (active && active.group != null) {
          setActive(active.group, active.current, active.label);
        }
        syncTargetHardwareUi();
        syncSpineLabels();
        savePersisted(state, getQty());
        refreshPrice();
        syncSoldOutOptions();
        await viewer.setState(state);
        if (active) focusPartForGroup(active.group);
      }

      function setSectionTitle(groupKey, title) {
        const section = controls.querySelector('[data-group="' + groupKey + '"]');
        if (!section) return;
        const h2 = section.querySelector('h2');
        if (h2) h2.textContent = title;
      }

      function hideOptionItem(item, hidden) {
        if (!item || !item.el) return;
        const cell =
          item.el.closest && item.el.closest('.arrow-playground__swatch-cell');
        if (cell) cell.hidden = !!hidden;
        else item.el.hidden = !!hidden;
      }

      function syncTargetHardwareUi() {
        const target = isTargetModel(state.model);
        ['insert', 'fieldPoint', 'lighted'].forEach(function (groupKey) {
          const section = controls.querySelector('[data-group="' + groupKey + '"]');
          if (section) section.hidden = target;
        });
        const odSection = controls.querySelector('[data-aluminum-od]');
        if (odSection) {
          odSection.hidden =
            target || !(state.insert && state.insertFinish === 'aluminum');
        }
        const lightedColor = controls.querySelector('[data-lighted-nock-color]');
        if (lightedColor) lightedColor.hidden = target || !state.lightedNocks;

        setSectionTitle('fieldPointGrain', target ? 'Glue-in point' : 'Point grain');
        setSectionTitle('nockColor', target ? 'Nock' : 'Nock color');

        const grainItems = optionButtons.get('fieldPointGrain') || [];
        const allow150 = !target && (state.fieldPointFinish || 'stainless') === 'stainless';
        grainItems.forEach(function (item) {
          const k = String(item.key);
          const isGlue = k === '120' || k === '200';
          let hide;
          if (target) hide = !isGlue;
          else hide = isGlue || (k === '150' && !allow150);
          hideOptionItem(item, hide);
        });
        const grainCurrent = String(currentPointGrain(state));
        setActive('fieldPointGrain', grainCurrent, grainCurrent + ' gr');

        const nockItems = optionButtons.get('nockColor') || [];
        nockItems.forEach(function (item) {
          hideOptionItem(item, target && String(item.key).toLowerCase() !== 'black');
        });
        if (target) setActive('nockColor', 'Black', 'Black bushing');
      }

      function laborModeLabel(mode, hasWrap) {
        if (mode === 'none') return 'None (bare / components only)';
        if (mode === 'wrap_only') return "I'll fletch — wrap install only ($12)";
        if (mode === 'diy') {
          return hasWrap ? "I'll fletch & install wraps" : "I'll fletch myself";
        }
        return hasWrap ? 'TitleX fletches ($48)' : 'TitleX fletches ($36)';
      }

      function syncLaborModeOptions() {
        const hasWrap = !!(state.wrapKey && state.wrapKey !== 'none');
        const mode = normalizeLaborMode(state.laborMode, hasWrap);
        if (mode !== state.laborMode) state.laborMode = mode;
        const items = optionButtons.get('laborMode') || [];
        items.forEach(function (item) {
          const label = laborModeLabel(item.key, hasWrap);
          item.el.textContent = label;
          item.el.title = label;
          item.el.hidden = item.key === 'wrap_only' && !hasWrap;
        });
        setActive('laborMode', mode, laborModeLabel(mode, hasWrap));
      }

      function optionGroup(title, groupKey, keys, current, onPick, labels, dense, note) {
        labels = labels || {};
        const section = sectionHead(
          title,
          groupKey,
          labels[current] || String(current),
          note
        );
        const useSegment = !dense && keys.length > 0 && keys.length <= 5;
        const wrap = document.createElement('div');
        wrap.className =
          'arrow-playground__options' +
          (useSegment ? ' arrow-playground__options--segment' : '') +
          (dense ? ' arrow-playground__options--dense arrow-playground__options--spec' : '');
        keys.forEach(function (key) {
          const baseLabel = labels[key] || String(key);
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className =
            'arrow-playground__option' + (key === current ? ' is-active' : '');
          btn.textContent = baseLabel;
          btn.title =
            groupKey === 'insert' && key === 'brass'
              ? '75 gr break-off to 50 gr'
              : baseLabel;
          btn.addEventListener('click', function () {
            if (btn.disabled) return;
            onPick(key, baseLabel);
          });
          rememberOption(groupKey, key, btn, baseLabel);
          wrap.appendChild(btn);
        });
        section.appendChild(wrap);
        controls.appendChild(section);
      }

      function modelPicker(current, onPick) {
        const labels = MODEL_PICKER_LABELS;
        const section = sectionHead('Model', 'model', labels[current] || String(current));
        MODEL_CLUSTERS.forEach(function (cluster) {
          const clusterEl = document.createElement('div');
          clusterEl.className = 'arrow-playground__model-cluster';
          const heading = document.createElement('h3');
          heading.className = 'arrow-playground__cluster-label';
          heading.textContent = cluster.title;
          clusterEl.appendChild(heading);
          const wrap = document.createElement('div');
          wrap.className = 'arrow-playground__options arrow-playground__options--segment';
          cluster.keys.forEach(function (key) {
            const baseLabel = labels[key] || String(key);
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className =
              'arrow-playground__option' + (key === current ? ' is-active' : '');
            btn.textContent = baseLabel;
            btn.title = baseLabel;
            btn.addEventListener('click', function () {
              if (btn.disabled) return;
              onPick(key, baseLabel);
            });
            rememberOption('model', key, btn, baseLabel);
            wrap.appendChild(btn);
          });
          clusterEl.appendChild(wrap);
          section.appendChild(clusterEl);
        });
        controls.appendChild(section);
      }

      function resolveWrapTextureUrl(textureUrl) {
        if (!textureUrl) return null;
        const raw = String(textureUrl).trim();
        if (!raw) return null;
        if (/^https?:\/\//i.test(raw)) return raw;
        if (raw.indexOf('//') === 0) return 'https:' + raw;
        const base = String(root.dataset.wrapTextureBase || '').trim();
        if (!base) return raw;
        return base.replace(/\/?$/, '/') + raw.replace(/^\//, '');
      }

      function wrapPreviewUrl(wrapKey, wrapCatalog) {
        if (!wrapKey || wrapKey === 'none') return null;
        const textures = (wrapCatalog && wrapCatalog.wrapTextures) || {};
        const colors = (wrapCatalog && wrapCatalog.wrapColors) || {};
        const base = wrapBaseHandle(wrapKey);
        let tex = textures[wrapKey];
        if (!tex && colors[base]) {
          const hit = colors[base].find(function (c) {
            return c.key === wrapKey;
          });
          tex = hit && hit.textureUrl;
        }
        return resolveWrapTextureUrl(tex);
      }

      function shortWrapLabel(label, key) {
        let text = String(label || key || '').trim();
        text = text
          .replace(/\s+arrow\s+wraps?/i, '')
          .replace(/\s+wraps?/i, '')
          .replace(/\s+/g, ' ')
          .trim();
        if (!text) text = String(key || 'Wrap');
        return text;
      }

      function wrapPicker(title, groupKey, wrapCatalog, current, onPick) {
        wrapCatalog = wrapCatalog || {};
        const wrapKeys = wrapCatalog.wrapKeys || [];
        const labels = wrapCatalog.wrapLabels || {};
        const wrapColors = wrapCatalog.wrapColors || {};
        const wrapBrands = wrapCatalog.wrapBrands || {};
        const wrapPhotos = wrapCatalog.wrapPhotos || {};
        let selectedKey = current || 'none';
        let brandFilter = 'all';
        const currentLabel =
          selectedKey === 'none'
            ? 'None'
            : shortWrapLabel(
                labels[selectedKey] || labels[wrapBaseHandle(selectedKey)],
                selectedKey
              );
        const section = sectionHead(title, groupKey, currentLabel);

        const warn = document.createElement('p');
        warn.className = 'arrow-playground__wrap-warning';
        warn.setAttribute('role', 'note');
        warn.textContent =
          'Wraps are applied to your dozen (not shipped separately). Custom wraps take a week before your arrows are built.';
        section.appendChild(warn);

        const toolbar = document.createElement('div');
        toolbar.className = 'arrow-playground__wrap-toolbar';
        const search = document.createElement('input');
        search.type = 'search';
        search.className = 'arrow-playground__wrap-search';
        search.placeholder = 'Search wraps…';
        search.setAttribute('aria-label', 'Search wraps');
        search.autocomplete = 'off';
        toolbar.appendChild(search);

        const brandChips = collectWrapBrandChips(wrapKeys, wrapBrands);
        const brandRow = document.createElement('div');
        brandRow.className =
          'arrow-playground__options arrow-playground__options--segment arrow-playground__wrap-brands';
        brandRow.setAttribute('role', 'group');
        brandRow.setAttribute('aria-label', 'Filter wraps by brand');
        if (brandChips.length > 1) {
          brandChips.forEach(function (chip) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className =
              'arrow-playground__option' + (chip.key === brandFilter ? ' is-active' : '');
            btn.textContent = chip.label;
            btn.dataset.wrapBrandChip = chip.key;
            btn.setAttribute('aria-pressed', chip.key === brandFilter ? 'true' : 'false');
            btn.addEventListener('click', function () {
              brandFilter = chip.key;
              Array.prototype.forEach.call(brandRow.children, function (el) {
                const active = el.dataset.wrapBrandChip === brandFilter;
                el.classList.toggle('is-active', active);
                el.setAttribute('aria-pressed', active ? 'true' : 'false');
              });
              filterWraps();
            });
            brandRow.appendChild(btn);
          });
          toolbar.appendChild(brandRow);
        }

        const empty = document.createElement('p');
        empty.className = 'arrow-playground__wrap-empty';
        empty.hidden = true;
        empty.textContent = 'No wraps match your filters.';

        const noneGrid = document.createElement('div');
        noneGrid.className = 'arrow-playground__wrap-grid arrow-playground__wrap-grid--none';

        const singleBlock = document.createElement('div');
        singleBlock.className = 'arrow-playground__wrap-group';
        const singleHead = document.createElement('h3');
        singleHead.className = 'arrow-playground__wrap-group-title';
        singleHead.textContent = 'Single color';
        const singleGrid = document.createElement('div');
        singleGrid.className = 'arrow-playground__wrap-grid';
        singleBlock.appendChild(singleHead);
        singleBlock.appendChild(singleGrid);

        const multiBlock = document.createElement('div');
        multiBlock.className = 'arrow-playground__wrap-group';
        const multiHead = document.createElement('h3');
        multiHead.className = 'arrow-playground__wrap-group-title';
        multiHead.textContent = 'Multi color';
        const multiGrid = document.createElement('div');
        multiGrid.className = 'arrow-playground__wrap-grid';
        multiBlock.appendChild(multiHead);
        multiBlock.appendChild(multiGrid);

        const colorSection = document.createElement('div');
        colorSection.className = 'arrow-playground__wrap-color-picker';
        colorSection.hidden = true;
        const colorHead = document.createElement('div');
        colorHead.className = 'arrow-playground__section-head';
        const colorTitle = document.createElement('h3');
        colorTitle.className = 'arrow-playground__wrap-color-title';
        colorTitle.textContent = 'Wrap color';
        const colorVal = document.createElement('span');
        colorVal.className = 'arrow-playground__section-value';
        colorHead.appendChild(colorTitle);
        colorHead.appendChild(colorVal);
        const colorSwatches = document.createElement('div');
        colorSwatches.className = 'arrow-playground__swatches';
        colorSection.appendChild(colorHead);
        colorSection.appendChild(colorSwatches);

        const allCards = [];

        function familyActiveKey(key) {
          if (key === 'none') return selectedKey === 'none' || !selectedKey;
          return wrapBaseHandle(selectedKey) === key;
        }

        function syncCardActive() {
          allCards.forEach(function (card) {
            card.classList.toggle('is-active', familyActiveKey(card.dataset.wrapKey));
          });
        }

        function syncColorPicker() {
          const base = wrapBaseHandle(selectedKey);
          const colors = selectedKey && selectedKey !== 'none' ? wrapColors[base] : null;
          colorSwatches.innerHTML = '';
          if (!colors || !colors.length) {
            colorSection.hidden = true;
            return;
          }
          colorSection.hidden = false;
          colorVal.textContent = wrapColorDisplayName(selectedKey) || '';
          colors.forEach(function (c) {
            const colorName = c.color || c.slug || 'Color';
            const soldOut = wrapColorSoldOut(base, colorName);
            const cell = document.createElement('div');
            cell.className = 'arrow-playground__swatch-cell';
            const btn = document.createElement('button');
            btn.type = 'button';
            const isLight = String(c.color || '').trim().toLowerCase() === 'white';
            btn.className =
              'arrow-playground__swatch' +
              (isLight ? ' arrow-playground__swatch--light' : '') +
              (c.key === selectedKey ? ' is-active' : '');
            btn.style.background = colorToHex(c.color || c.slug);
            btn.addEventListener('click', function () {
              if (btn.disabled) return;
              selectedKey = c.key;
              const label = labels[c.key] || c.label || c.key;
              syncCardActive();
              syncColorPicker();
              onPick(c.key, label);
            });
            const cap = document.createElement('span');
            cap.className = 'arrow-playground__swatch-caption';
            cap.textContent = colorName;
            cell.appendChild(btn);
            cell.appendChild(cap);
            applySoldOutUi(btn, soldOut, colorName);
            colorSwatches.appendChild(cell);
          });
        }

        function appendWrapThumbImg(parent, url) {
          const img = document.createElement('img');
          img.className = 'arrow-playground__wrap-thumb';
          img.src = url;
          img.alt = '';
          img.decoding = 'async';
          img.loading = 'lazy';
          parent.appendChild(img);
          return img;
        }

        function paintWrapThumb(thumbFrame, key) {
          if (key === 'none') {
            const thumb = document.createElement('span');
            thumb.className =
              'arrow-playground__wrap-thumb arrow-playground__wrap-thumb--none';
            thumb.textContent = 'None';
            thumbFrame.appendChild(thumb);
            return;
          }

          const photoUrl = wrapPhotos[key];
          if (!photoUrl) return;

          thumbFrame.setAttribute('role', 'img');
          thumbFrame.setAttribute('aria-hidden', 'true');
          thumbFrame.classList.add('arrow-playground__wrap-thumb-frame--photo');
          appendWrapThumbImg(thumbFrame, photoUrl);
        }

        function makeWrapCard(key) {
          const label = labels[key] || String(key);
          const display = key === 'none' ? 'None' : shortWrapLabel(label, key);
          const colors = wrapColors[key] || [];
          const isMulti = colors.length >= 2;
          const brandInfo = wrapBrands[key] || {};
          const brandKey = brandInfo.brandKey || wrapBrandKey(brandInfo.vendor) || '';
          const brandName = brandInfo.label || wrapBrandLabel(brandKey, brandInfo.vendor);
          const colorNames = colors
            .map(function (c) {
              return c.color || c.slug;
            })
            .join(' ');

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className =
            'arrow-playground__wrap-card' + (familyActiveKey(key) ? ' is-active' : '');
          btn.dataset.wrapKey = key;
          btn.dataset.wrapBrand = brandKey;
          btn.dataset.wrapSearch = wrapSearchNormalize(
            label +
              ' ' +
              key +
              ' ' +
              colorNames +
              ' ' +
              brandName +
              ' ' +
              (brandInfo.vendor || '')
          );
          btn.title = label;
          btn.setAttribute('aria-label', label);

          const thumbFrame = document.createElement('span');
          thumbFrame.className = 'arrow-playground__wrap-thumb-frame';
          paintWrapThumb(thumbFrame, key);
          btn.appendChild(thumbFrame);

          const text = document.createElement('span');
          text.className = 'arrow-playground__wrap-label';
          text.textContent = display;
          btn.appendChild(text);

          if (isMulti) {
            const hint = document.createElement('span');
            hint.className = 'arrow-playground__wrap-color-count';
            hint.textContent = colors.length + ' colors';
            btn.appendChild(hint);
          }

          const catalogHandle = key === 'none' ? null : key;
          if (catalogHandle && catalog && catalog.wraps && catalog.wraps[catalogHandle]) {
            const pair = pickWrapPair(catalog.wraps[catalogHandle], state.model || 'tx5');
            const bowtiqueCents = pair ? pair.price12 || pair.price6 : 0;
            const install = pickInstallVariant(catalog, bowtiqueCents);
            const cents = install ? install.price : bowtiqueCents;
            if (cents) {
              const priceEl = document.createElement('span');
              priceEl.className = 'arrow-playground__wrap-price';
              priceEl.textContent = formatMoney(cents, catalog.moneyFormat);
              btn.appendChild(priceEl);
            }
          }

          const stockEl = document.createElement('span');
          stockEl.className = 'arrow-playground__wrap-stock';
          stockEl.hidden = true;
          btn.appendChild(stockEl);

          rememberOption('wrap', key, btn, display);

          btn.addEventListener('click', function () {
            if (btn.disabled) return;
            let nextKey = key;
            let nextLabel = label;
            if (key !== 'none' && colors.length) {
              if (wrapBaseHandle(selectedKey) === key && wrapColorSlug(selectedKey)) {
                nextKey = selectedKey;
                nextLabel = labels[selectedKey] || label;
              } else {
                const inStock = colors.filter(function (c) {
                  return !wrapColorSoldOut(key, c.color || c.slug);
                });
                nextKey = defaultWrapColorKey(key, inStock.length ? inStock : colors);
                nextLabel = labels[nextKey] || label;
              }
            }
            selectedKey = nextKey;
            syncCardActive();
            syncColorPicker();
            onPick(nextKey, nextLabel);
          });
          allCards.push(btn);
          return { btn: btn, isMulti: isMulti };
        }

        wrapKeys.forEach(function (key) {
          const made = makeWrapCard(key);
          if (key === 'none') {
            noneGrid.appendChild(made.btn);
            return;
          }
          if (made.isMulti) multiGrid.appendChild(made.btn);
          else singleGrid.appendChild(made.btn);
        });

        function filterWraps() {
          const q = wrapSearchNormalize(search.value);
          let visibleProducts = 0;
          let visibleSingle = 0;
          let visibleMulti = 0;
          allCards.forEach(function (card) {
            const key = card.dataset.wrapKey;
            if (key === 'none') {
              card.hidden = false;
              return;
            }
            const hay = card.dataset.wrapSearch || '';
            const brandOk = brandFilter === 'all' || card.dataset.wrapBrand === brandFilter;
            const searchOk = !q || hay.indexOf(q) >= 0;
            const show = brandOk && searchOk;
            card.hidden = !show;
            if (!show) return;
            visibleProducts += 1;
            if (card.parentNode === multiGrid) visibleMulti += 1;
            else visibleSingle += 1;
          });
          singleBlock.hidden = visibleSingle === 0;
          multiBlock.hidden = visibleMulti === 0;
          empty.hidden = visibleProducts > 0;
        }

        search.addEventListener('input', filterWraps);

        section.appendChild(toolbar);
        section.appendChild(empty);
        section.appendChild(noneGrid);
        section.appendChild(singleBlock);
        section.appendChild(multiBlock);
        section.appendChild(colorSection);
        controls.appendChild(section);
        syncColorPicker();
        filterWraps();
      }

      function cutLengthSelect(current, onPick) {
        const cur = current == null ? 32 : Number(current);
        const label = cur >= 32 ? 'Full Shaft' : cur + '"';
        const section = sectionHead('Cut length', 'cutLength', label);
        const select = document.createElement('select');
        select.className = 'arrow-playground__select';
        select.setAttribute('aria-label', 'Finished arrow length');

        const full = document.createElement('option');
        full.value = '32';
        full.textContent = 'Full Shaft (No Cut)';
        select.appendChild(full);

        for (let q = 20 * 4; q <= 32 * 4; q++) {
          const inches = q / 4;
          if (inches === 32) continue;
          const opt = document.createElement('option');
          opt.value = String(inches);
          opt.textContent = inches + '"';
          select.appendChild(opt);
        }

        select.value = String(cur);
        if (
          ![].slice.call(select.options).some(function (o) {
            return o.value === select.value;
          })
        ) {
          select.value = '32';
        }

        select.addEventListener('change', function () {
          const inches = parseFloat(select.value);
          const nextLabel = inches >= 32 ? 'Full Shaft' : inches + '"';
          setActive('cutLength', inches, nextLabel);
          onPick(inches);
        });
        section.appendChild(select);
        controls.appendChild(section);
      }

      function swatchGroup(title, groupKey, current, onPick, names) {
        const section = sectionHead(title, groupKey, current);
        const wrap = document.createElement('div');
        wrap.className = 'arrow-playground__swatches';
        (names || VANE_COLOR_NAMES).forEach(function (name) {
          const cell = document.createElement('div');
          cell.className = 'arrow-playground__swatch-cell';
          const btn = document.createElement('button');
          btn.type = 'button';
          const isLight = /^(white|yellow|tan)$/.test(String(name).trim().toLowerCase());
          btn.className =
            'arrow-playground__swatch' +
            (isLight ? ' arrow-playground__swatch--light' : '') +
            (name === current ? ' is-active' : '');
          btn.style.background = colorToHex(name);
          btn.title = name;
          btn.setAttribute('aria-label', name);
          btn.addEventListener('click', function () {
            if (btn.disabled) return;
            onPick(name);
          });
          const cap = document.createElement('span');
          cap.className = 'arrow-playground__swatch-caption';
          cap.textContent = name;
          cell.appendChild(btn);
          cell.appendChild(cap);
          rememberOption(groupKey, name, btn, name);
          wrap.appendChild(cell);
        });
        section.appendChild(wrap);
        controls.appendChild(section);
        return section;
      }

      function refreshVaneSwatches() {
        ['vanePrimary', 'vaneSecondary'].forEach(function (key) {
          const old = controls.querySelector('[data-group="' + key + '"]');
          if (old) old.remove();
          optionButtons.delete(key);
        });
        const names = vaneColorNames(state.vaneStyle);
        const anchor = controls.querySelector('[data-group="nockColor"]');
        const primary = swatchGroup('Vane primary', 'vanePrimary', state.vanePrimary, function (vanePrimary) {
          apply(
            { vanePrimary: vanePrimary },
            { group: 'vanePrimary', current: vanePrimary, label: vanePrimary }
          );
        }, names);
        const secondary = swatchGroup(
          'Vane secondary',
          'vaneSecondary',
          state.vaneSecondary,
          function (vaneSecondary) {
            apply(
              { vaneSecondary: vaneSecondary },
              { group: 'vaneSecondary', current: vaneSecondary, label: vaneSecondary }
            );
          },
          names
        );
        if (anchor) {
          controls.insertBefore(primary, anchor);
          controls.insertBefore(secondary, anchor);
        }
      }

      function wrapStorefrontImage(product) {
        if (!product) return '';
        if (product.featured_image) return String(product.featured_image);
        const images = product.images || [];
        if (images.length) {
          const first = images[0];
          if (typeof first === 'string') return first;
          if (first && first.src) return String(first.src);
        }
        if (product.image && product.image.src) return String(product.image.src);
        return '';
      }

      async function fillWrapPhotosFromStorefront(wrapPhotos, handles, collectionHandle) {
        const need = {};
        (handles || []).forEach(function (h) {
          if (h) need[h] = true;
        });
        const coll = String(collectionHandle || '').replace(/^\/+|\/+$/g, '');
        if (coll) {
          try {
            const res = await fetch(
              '/collections/' + encodeURIComponent(coll) + '/products.json?limit=250',
              { credentials: 'same-origin' }
            );
            if (res.ok) {
              const data = await res.json();
              (data.products || []).forEach(function (p) {
                const handle = p && p.handle;
                const src = wrapStorefrontImage(p);
                if (handle && src) wrapPhotos[handle] = src;
                if (handle) delete need[handle];
              });
            }
          } catch (e) {
            /* fall through to per-product fetch */
          }
        }
        const leftover = Object.keys(need);
        const limit = 6;
        for (let i = 0; i < leftover.length; i += limit) {
          const batch = leftover.slice(i, i + limit);
          await Promise.all(
            batch.map(function (handle) {
              return fetch('/products/' + encodeURIComponent(handle) + '.js', {
                credentials: 'same-origin',
              })
                .then(function (res) {
                  return res.ok ? res.json() : null;
                })
                .then(function (p) {
                  const src = wrapStorefrontImage(p);
                  if (src) wrapPhotos[handle] = src;
                })
                .catch(function () {
                  /* skip */
                });
            })
          );
        }
      }

      function bustWrapsManifestUrl(url) {
        if (!url) return url;
        try {
          const u = new URL(url, window.location.origin);
          u.searchParams.set('_tx', String(Math.floor(Date.now() / 60000)));
          return u.href;
        } catch (e) {
          return url + (String(url).indexOf('?') >= 0 ? '&' : '?') + '_tx=' + Date.now();
        }
      }
      async function loadWrapCatalog() {
        const wrapLabels = { none: 'None' };
        const wrapTextures = {};
        const wrapColors = {};
        const wrapStitchUrls = {};
        const wrapBrands = {};
        const wrapPhotos = {};
        let manifestKeys = [];
        try {
          const url = bustWrapsManifestUrl(root.dataset.wrapsManifest);
          if (url) {
            const res = await fetch(url, { cache: 'no-store' });
            if (res.ok) {
              const man = await res.json();
              const all = man.wraps || {};
              Object.keys(all).forEach(function (k) {
                if (k.indexOf('__') < 0) return;
                if (isHiddenWrapHandle(k)) return;
                const entry = all[k] || {};
                if (!(entry.textureUrl && String(entry.textureUrl).trim())) return;
                const base = wrapBaseHandle(k);
                if (!wrapColors[base]) wrapColors[base] = [];
                wrapColors[base].push({
                  key: k,
                  slug: wrapColorSlug(k),
                  color: entry.color || wrapColorDisplayName(k),
                  textureUrl: resolveWrapTextureUrl(entry.textureUrl) || entry.textureUrl,
                  label: entry.label || k,
                });
                wrapLabels[k] = entry.label || k;
                wrapTextures[k] = resolveWrapTextureUrl(entry.textureUrl) || entry.textureUrl;
              });
              Object.keys(wrapColors).forEach(function (base) {
                wrapColors[base].sort(function (a, b) {
                  return String(a.color).localeCompare(String(b.color));
                });
              });
              manifestKeys = Object.keys(all).filter(function (k) {
                if (k === 'none' || k.indexOf('__') >= 0 || k.indexOf('wrap-sample') === 0) {
                  return false;
                }
                if (isHiddenWrapHandle(k)) return false;
                const entry = all[k] || {};
                if (entry.textureUrl && String(entry.textureUrl).trim()) return true;
                return !!(wrapColors[k] && wrapColors[k].length);
              });
              manifestKeys.sort();
              manifestKeys.forEach(function (k) {
                const entry = all[k] || {};
                wrapLabels[k] = entry.label || k;
                if (entry.textureUrl) {
                  wrapTextures[k] = resolveWrapTextureUrl(entry.textureUrl) || entry.textureUrl;
                } else if (wrapColors[k] && wrapColors[k][0]) {
                  wrapTextures[k] = wrapColors[k][0].textureUrl;
                }
                if (entry.stitchUrl && String(entry.stitchUrl).trim()) {
                  wrapStitchUrls[k] = entry.stitchUrl;
                }
              });
            }
          }
        } catch (e) {
          /* keep collection / empty */
        }

        const products = catalog && catalog.wraps ? catalog.wraps : {};
        const productHandles = Object.keys(products)
          .filter(Boolean)
          .filter(function (h) {
            return !isHiddenWrapHandle(h);
          })
          .sort(function (a, b) {
            const ta = String((products[a] && products[a].title) || a);
            const tb = String((products[b] && products[b].title) || b);
            return ta.localeCompare(tb);
          });
        const manifestBases = {};
        Object.keys(wrapColors).forEach(function (k) {
          if (k) manifestBases[k] = true;
        });
        Object.keys(wrapTextures).forEach(function (k) {
          if (k && k.indexOf('__') < 0) manifestBases[k] = true;
        });
        const manifestBaseList = Object.keys(manifestBases);

        productHandles.forEach(function (h) {
          const p = products[h] || {};
          wrapLabels[h] = p.title || wrapLabels[h] || h;
          if (p.image) wrapPhotos[h] = p.image;
          const vendor = p.vendor || '';
          const brandKey = wrapBrandKey(vendor);
          wrapBrands[h] = {
            vendor: vendor,
            brandKey: brandKey,
            label: wrapBrandLabel(brandKey, vendor),
          };
          const mk = matchWrapManifestKey(h, manifestBaseList);
          if (!mk) return;
          if (!wrapColors[h] && wrapColors[mk]) wrapColors[h] = wrapColors[mk];
          if (!wrapTextures[h] && wrapTextures[mk]) wrapTextures[h] = wrapTextures[mk];
          if (!wrapStitchUrls[h] && wrapStitchUrls[mk]) wrapStitchUrls[h] = wrapStitchUrls[mk];
          if (!wrapPhotos[h] && wrapPhotos[mk]) wrapPhotos[h] = wrapPhotos[mk];
        });

        const missingPhotos = productHandles.filter(function (h) {
          return h && !wrapPhotos[h];
        });
        if (missingPhotos.length) {
          await fillWrapPhotosFromStorefront(
            wrapPhotos,
            missingPhotos,
            root.dataset.wrapsCollection
          );
        }

        return {
          wrapKeys: ['none'].concat(productHandles.length ? productHandles : manifestKeys),
          wrapLabels: wrapLabels,
          wrapTextures: wrapTextures,
          wrapColors: wrapColors,
          wrapStitchUrls: wrapStitchUrls,
          wrapBrands: wrapBrands,
          wrapPhotos: wrapPhotos,
        };
      }

      async function buildControls() {
        optionButtons.clear();
        controls.innerHTML = '';

        modelPicker(state.model, function (model, label) {
          apply(modelSwitchPatch(state, model), {
            group: 'model',
            current: model,
            label: label,
          });
        });
        optionGroup(
          'Spine',
          'spine',
          SPINE_KEYS,
          state.spine,
          function (spine, label) {
            apply({ spine: spine }, { group: 'spine', current: spine, label: label });
          },
          spineLabelsFor(state.model),
          true,
          'Straightness ' + SHAFT_STRAIGHTNESS
        );
        optionGroup(
          'Fletch',
          'fletch',
          [3, 4],
          state.fletchCount,
          function (fletchCount, label) {
            apply(
              { fletchCount: fletchCount },
              { group: 'fletch', current: fletchCount, label: label }
            );
          },
          { 3: '3-Fletch', 4: '4-Fletch' }
        );
        optionGroup(
          'Helical',
          'helical',
          ['left', 'right', 'none'],
          state.helical,
          function (helical, label) {
            apply({ helical: helical }, { group: 'helical', current: helical, label: label });
          },
          { left: 'Left', right: 'Right', none: 'None' }
        );
        optionGroup(
          'Vanes',
          'vaneProduct',
          ['hybrid', 'alpha'],
          state.vaneStyle === 'alpha' ? 'alpha' : 'hybrid',
          function (vaneStyle) {
            const names = vaneColorNames(vaneStyle);
            const patch = { vaneStyle: vaneStyle };
            if (names.indexOf(state.vanePrimary) < 0) {
              patch.vanePrimary = vaneStyle === 'alpha' ? 'Yellow' : 'Bright Green';
            }
            if (names.indexOf(state.vaneSecondary) < 0) {
              patch.vaneSecondary = vaneStyle === 'alpha' ? 'White' : 'OD Green';
            }
            apply(patch, {
              group: 'vaneProduct',
              current: vaneStyle,
              label: vaneSpecLabel(vaneStyle),
            }).then(function () {
              refreshVaneSwatches();
            });
          },
          { hybrid: 'AAE Hybrid 26', alpha: 'Bohning Alpha' }
        );
        setActive('vaneProduct', state.vaneStyle === 'alpha' ? 'alpha' : 'hybrid', vaneSpecLabel(state.vaneStyle));
        swatchGroup('Vane primary', 'vanePrimary', state.vanePrimary, function (vanePrimary) {
          apply(
            { vanePrimary: vanePrimary },
            { group: 'vanePrimary', current: vanePrimary, label: vanePrimary }
          );
        }, vaneColorNames(state.vaneStyle));
        swatchGroup('Vane secondary', 'vaneSecondary', state.vaneSecondary, function (vaneSecondary) {
          apply(
            { vaneSecondary: vaneSecondary },
            { group: 'vaneSecondary', current: vaneSecondary, label: vaneSecondary }
          );
        }, vaneColorNames(state.vaneStyle));
        swatchGroup(
          'Nock color',
          'nockColor',
          state.nockColor,
          function (nockColor) {
            apply(
              { nockColor: nockColor },
              { group: 'nockColor', current: nockColor, label: nockColor }
            );
          },
          NOCK_COLOR_NAMES
        );
        optionGroup(
          'Lighted nocks',
          'lighted',
          ['no', 'yes'],
          state.lightedNocks ? 'yes' : 'no',
          function (v, label) {
            const on = v === 'yes';
            const nextColor =
              LIGHTED_NOCK_COLOR_NAMES.indexOf(state.lightedNockColor) >= 0
                ? state.lightedNockColor
                : 'Bright Green';
            apply(
              { lightedNocks: on, lightedNockColor: nextColor },
              { group: 'lighted', current: v, label: label }
            );
          },
          {
            no: 'Standard only',
            yes: 'Add lighted',
          }
        );
        const lightedColorSection = swatchGroup(
          'Lighted nock color',
          'lightedNockColor',
          state.lightedNockColor || 'Bright Green',
          function (lightedNockColor) {
            apply(
              { lightedNockColor: lightedNockColor },
              {
                group: 'lightedNockColor',
                current: lightedNockColor,
                label: lightedNockColor,
              }
            );
          },
          LIGHTED_NOCK_COLOR_NAMES
        );
        lightedColorSection.dataset.lightedNockColor = '1';
        lightedColorSection.hidden = !state.lightedNocks;
        optionGroup(
          'Insert',
          'insert',
          ['off', 'brass', 'stainless', 'aluminum'],
          state.insert ? state.insertFinish : 'off',
          function (v, label) {
            if (v === 'off') {
              apply({ insert: false }, { group: 'insert', current: v, label: label });
            } else {
              apply(
                {
                  insert: true,
                  insertFinish: v,
                  insertAluminumOd: state.insertAluminumOd || '6.8mm',
                },
                { group: 'insert', current: v, label: label }
              );
            }
          },
          insertLabels(),
          true
        );
        optionGroup(
          'Half out OD',
          'insertAluminumOd',
          ['6.8mm', '7.1mm'],
          state.insertAluminumOd || '6.8mm',
          function (v, label) {
            apply(
              { insertAluminumOd: v },
              { group: 'insertAluminumOd', current: v, label: label }
            );
          },
          { '6.8mm': '6.8mm', '7.1mm': '7.1mm' }
        );
        const odSection = controls.lastElementChild;
        if (odSection) {
          odSection.dataset.aluminumOd = '1';
          odSection.hidden = !(state.insert && state.insertFinish === 'aluminum');
        }
        optionGroup(
          'Field point',
          'fieldPoint',
          ['none', 'stainless', 'black'],
          state.fieldPointFinish || 'stainless',
          function (v, label) {
            const patch = { fieldPointFinish: v, fieldPoint: v !== 'none' };
            if (v !== 'stainless' && normalizeFieldPointGrain(state.fieldPointGrain) === 150) {
              patch.fieldPointGrain = 125;
            }
            apply(patch, { group: 'fieldPoint', current: v, label: label });
          },
          {
            none: 'None',
            stainless: 'Stainless',
            black: 'Black',
          }
        );
        optionGroup(
          'Point grain',
          'fieldPointGrain',
          ['100', '125', '150', '120', '200'],
          String(currentPointGrain(state)),
          function (v, label) {
            let grain;
            if (isTargetModel(state.model)) grain = normalizeGlueInGrain(v);
            else grain = fieldPointGrainForFinish(v, state.fieldPointFinish || 'stainless');
            apply(
              { fieldPointGrain: grain },
              { group: 'fieldPointGrain', current: String(grain), label: grain + ' gr' }
            );
          },
          {
            '100': '100 gr',
            '125': '125 gr',
            '150': '150 gr',
            '120': '120 gr',
            '200': '200 gr',
          }
        );
        syncTargetHardwareUi();
        cutLengthSelect(state.cutLength, function (cutLength) {
          apply({ cutLength: cutLength });
        });

        const wrapCatalog = await loadWrapCatalog();
        if (
          state.wrapKey &&
          state.wrapKey !== 'none' &&
          wrapCatalog.wrapColors &&
          wrapCatalog.wrapColors[wrapBaseHandle(state.wrapKey)] &&
          !wrapColorSlug(state.wrapKey)
        ) {
          state.wrapKey = defaultWrapColorKey(
            wrapBaseHandle(state.wrapKey),
            wrapCatalog.wrapColors[wrapBaseHandle(state.wrapKey)]
          );
        }
        state.wrapUrl =
          state.wrapKey && state.wrapKey !== 'none'
            ? wrapPreviewUrl(state.wrapKey, wrapCatalog)
            : null;
        wrapPicker(
          'Wrap',
          'wrap',
          wrapCatalog,
          state.wrapKey || 'none',
          function (wrapKey, label) {
            const hasWrap = wrapKey && wrapKey !== 'none';
            apply(
              {
                wrapKey: hasWrap ? wrapKey : null,
                wrapUrl: wrapPreviewUrl(wrapKey, wrapCatalog),
                laborMode: 'titlex',
              },
              {
                group: 'wrap',
                current: wrapKey,
                label: wrapKey === 'none' ? 'None' : shortWrapLabel(label, wrapKey),
              }
            );
          }
        );

        syncSoldOutOptions();
        await viewer.setState(state);
        savePersisted(state, getQty());
      }

      root.querySelectorAll('[data-qty]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          const delta = btn.getAttribute('data-qty') === '+' ? 6 : -6;
          setQty(getQty() + delta);
          savePersisted(state, getQty());
          refreshPrice();
        });
      });

      if (qtyInput) {
        qtyInput.addEventListener('change', function () {
          setQty(getQty());
          savePersisted(state, getQty());
          refreshPrice();
        });
      }

      if (atcBtn) {
        atcBtn.addEventListener('click', function () {
          setError('');
          atcBtn.disabled = true;
          const originalLabel = atcBtn.textContent;
          atcBtn.textContent = 'Adding…';
          savePersisted(state, getQty());
          addBuildToCart(state, getQty(), catalog, sessionBuildId, viewer, host)
            .then(function (result) {
              if (result && result.state) {
                priceEl &&
                  (priceEl.textContent = formatMoney(
                    computeTotal(result.state),
                    catalog && catalog.moneyFormat
                  ));
              }
              atcBtn.textContent = 'Added ✓';
              window.setTimeout(function () {
                atcBtn.textContent = originalLabel || 'Add to cart';
                atcBtn.disabled = false;
              }, 1400);
            })
            .catch(function (err) {
              console.error('[Arrow Playground] add to cart failed', err);
              setError(err && err.message ? err.message : 'Could not add to cart.');
              atcBtn.textContent = originalLabel || 'Add to cart';
              atcBtn.disabled = false;
            });
        });
      }

      window.addEventListener('pagehide', function () {
        savePersisted(state, getQty());
      });

      function setCamera(orbit, fov, target, opts) {
        const mv = viewer.getViewer();
        if (!mv) return;
        // Close enough to inspect a part, but leave orbit / pan / zoom to the shopper.
        try {
          mv.setAttribute('camera-controls', '');
          mv.removeAttribute('disable-pan');
          mv.removeAttribute('disable-zoom');
          mv.setAttribute('touch-action', 'none');
          mv.setAttribute('min-camera-orbit', 'auto auto 0.05m');
          mv.setAttribute('max-camera-orbit', 'auto auto 400%');
          mv.setAttribute('min-field-of-view', '10deg');
          mv.setAttribute('max-field-of-view', '45deg');
        } catch (e) {
          /* ignore */
        }
        // "auto" is not interpolatable — pin the current look-at first so the
        // next orbit/target change pans along the shaft instead of zooming 0,0,0.
        if (opts && opts.animate && typeof mv.getCameraTarget === 'function') {
          const attr = String(mv.getAttribute('camera-target') || mv.cameraTarget || '');
          if (!attr || attr === 'auto') {
            try {
              const t = mv.getCameraTarget();
              if (t && Number.isFinite(t.x)) {
                const pinned = t.x + 'm ' + t.y + 'm ' + t.z + 'm';
                mv.cameraTarget = pinned;
                mv.setAttribute('camera-target', pinned);
                if (typeof mv.jumpCameraToGoal === 'function') mv.jumpCameraToGoal();
              }
            } catch (ePin) {
              /* ignore */
            }
          }
        }
        if (orbit) {
          mv.cameraOrbit = orbit;
          try {
            mv.setAttribute('camera-orbit', orbit);
          } catch (eOrb) {
            /* ignore */
          }
        }
        if (fov) {
          mv.fieldOfView = fov;
          try {
            mv.setAttribute('field-of-view', fov);
          } catch (eFov) {
            /* ignore */
          }
        }
        if (target) {
          mv.cameraTarget = target;
          try {
            mv.setAttribute('camera-target', target);
          } catch (eTgt) {
            /* ignore */
          }
        }
        if (opts && opts.animate) return;
        try {
          if (typeof mv.jumpCameraToGoal === 'function') mv.jumpCameraToGoal();
        } catch (e) {
          /* ignore */
        }
      }

      /** Frame the part being edited, then leave orbit/pan/zoom to the shopper. */
      function focusPartForGroup(group) {
        if (!shouldFocusPartCamera(group)) return;
        const part = controlGroupToPart(group);
        const cam = part && PART_CAMERAS[part];
        if (cam) setCamera(cam.orbit, cam.fov, cam.target, { animate: true });
      }

      function onControlsEdit(ev) {
        focusPartForGroup(groupFromControlTarget(ev.target));
      }
      controls.addEventListener('pointerdown', onControlsEdit);
      controls.addEventListener('focusin', onControlsEdit);

      function focusDeepLinkPart(part, viewerState) {
        const cam = PART_CAMERAS[part] || PART_CAMERAS.shaft;
        setCamera(cam.orbit, cam.fov, cam.target);

        let group = 'spine';
        if (part === 'wrap') group = 'wrap';
        else if (part === 'vane') group = 'vanePrimary';
        else if (part === 'nock') group = viewerState.lightedNocks ? 'lighted' : 'nockColor';
        else if (part === 'insert') group = isTargetModel(viewerState.model) ? 'fieldPointGrain' : 'insert';
        else if (part === 'point') group = isTargetModel(viewerState.model) ? 'fieldPointGrain' : 'fieldPoint';
        else if (part === 'shaft') group = 'model';

        const section = controls.querySelector('[data-group="' + group + '"]');
        if (section) {
          section.classList.add('is-deeplink-focus');
          try {
            section.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch (e) {
            section.scrollIntoView();
          }
          window.setTimeout(function () {
            section.classList.remove('is-deeplink-focus');
          }, 3200);
        }

        if (part === 'wrap' && viewerState.wrapKey && viewerState.wrapKey !== 'none') {
          const base = wrapBaseHandle(viewerState.wrapKey);
          const card = controls.querySelector('[data-wrap-key="' + base + '"]');
          if (card) {
            window.setTimeout(function () {
              try {
                card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              } catch (e2) {
                /* ignore */
              }
            }, 200);
          }
        }
      }

      // Capture / QA hook for teaser tooling (Playwright). Not used by storefront UI.
      window.__ARROW_PLAYGROUND__ = {
        viewer: viewer,
        getState: function () {
          return Object.assign({}, state);
        },
        apply: apply,
        setCamera: setCamera,
        ready: viewer.ready,
      };

      viewer.ready.then(function () {
        return buildControls();
      }).then(function () {
        refreshPrice();
        if (deepLink) {
          savePersisted(state, getQty());
          focusDeepLinkPart(deepLink.part, state);
        }
      });
    }

    bootViewer();
  });
})();
